// Réponses locales des modules de démonstration de la régie.
import { reponseDemoCatalogage } from './demoCatalogage.js';
import { reponseDemoPresentation } from './demoPresentation.js';
import { identifiantsDemo, lotDemo } from './demoLotOeuvres.js';
import { annulationCertificatSchema, type CleModele } from '@artosera/shared';
import type { ModeleDocument, CorpsMiseAJourModele } from './ventes.js';
import { ApiError } from './errors.js';
import { reponseAccesVenioDemo } from './demoAccesVenio.js';
import { deuxiemeFacteurDemoActif, reponseDeuxFacteursDemo } from './demoDeuxFacteurs.js';
import { reponseBoutiqueDemo } from './demoBoutique.js';
import type { ReponseAccesAssistant } from './assistantMcp.js';
import type { CorpsJeton, JetonPresente } from './accesApi.js';
import type { PlanComptable } from './comptabilite.js';
import { apercuFecDemo, lirePlanDemo, ecrirePlanDemo } from './demoComptabilite.js';
import { DEMO_JETONS } from './demoFixtures.js';
import type { CorpsListe, CorpsCampagne, VisuelNewsletter } from './newsletter.js';
import pngNewsletterCarre from './demo-artworks/newsletter-carre.png?url';
import pngNewsletterVertical from './demo-artworks/newsletter-vertical.png?url';
import { reponseDemoAssistant } from './demoAssistantReponse.js';
// Studio : simulation locale, sans modifier les droits de démonstration.
import { reponseDemoStudio } from './demoStudio.js';
import { reponseDemoIa } from './demoIa.js';
import { reponseDemoAssistantRecherche } from './demoAssistantRecherche.js';
import { reponseDemoDemandeSite } from './demoDemandeSite.js';
import { reponseDemoDiffusion } from './demoDiffusion.js';
import { reponseDemoContrats } from './demoContrats.js';
import { ADDONS_DEMONSTRATION, dormantesDemo, reponseDemoPilotage } from './demoPilotage.js';
import type { CorpsProvenance, CorpsCout } from './provenance.js';
import type { CorpsDossierOeuvres } from './dossiersOeuvres.js';
import type { CorpsSalle, SallePresentee } from './viewingRooms.js';
import type { CatalogueVisiteMiseAJour } from '@artosera/shared';
import { etatCatalogue, prixAffiche } from '@artosera/shared';
import type { MembreEquipe } from './equipe.js';
import {
  offresDemo,
  mutationOffresDemo,
  reservationsOffresDemo,
  facturesOffresDemo,
} from './demoOffres.js';
import {
  actionsPermises,
  reglageHorsLigne,
  AUCUN_DROIT,
  modulesDeLaGalerie,
  modulesDisponibles,
  modulesAVenir,
  modulesDuPalier,
  MODULES_DEMONSTRATION,
  type Module,
  type ModuleOptionnel,
  type ModulePilotable,
  type PalierModule,
  STATUTS_OEUVRE,
  sujet,
} from '@artosera/shared';
import type { ArtistePresente } from './artistes.js';
import type { SiteData } from '@artosera/site-renderer';
import type { IdentifiantsEnAttente } from '../routes/connexion/connexionFlow.js';
import type { ReponseMoi } from './types.js';
import {
  DEMO_NEWSLETTER_LISTES,
  DEMO_NEWSLETTER_CAMPAGNES,
  DEMO_NEWSLETTER_VISUELS,
  DEMO_NEWSLETTER_REGLAGES,
  DEMO_AGENDA,
  DEMO_PRESSE,
  DEMO_RENDEZ_VOUS,
  DEMO_PROVENANCE,
  DEMO_COUTS,
  DEMO_DOSSIERS_OEUVRES,
  DEMO_ACTUALITES,
  DEMO_VIEWING_ROOMS,
  DEMO_CATALOGUES_VISITE,
  DEMO_ARCHIVES,
  DEMO_ARTISTES,
  DEMO_CERTIFICATS_INITIAUX,
  DEMO_CONTACTS,
  DEMO_CONSOLE_GROUPE,
  DEMO_CONNECTEURS,
  DEMO_DEMANDES,
  DEMO_DOCUMENT_ACCESS,
  DEMO_MOUVEMENTS,
  DEMO_DOCUMENTS,
  DEMO_EQUIPE,
  DEMO_IDENTITES_SSO,
  DEMO_EXPOSITIONS,
  DEMO_FACTURES,
  DEMO_GALERIE,
  DEMO_LIEUX,
  DEMO_MEDIAS,
  DEMO_MEDIAS_GALERIE,
  DEMO_MODELE_FACTURE,
  DEMO_OEUVRES,
  DEMO_PAGES,
  DEMO_PDF_DOCUMENT_DATA_URI,
  DEMO_PDF_FACTURE_DATA_URI,
  DEMO_PROFIL_FISCAL,
  DEMO_PUBLICATIONS,
  DEMO_REGLEMENTS,
  DEMO_RESERVATIONS,
  DEMO_SITE,
  DEMO_SITE_DATA,
  DEMO_VIGNETTES,
} from './demoFixtures.js';
import type { CertificatPresente, CorpsEmissionCertificat } from './documentsGalerie.js';
import type {
  CorpsMiseAJourPage,
  CorpsMiseAJourSite,
  CorpsRedirection,
  PageSite,
  PublicationPresentee,
  ReglagesSite,
  RubriquePage,
} from './site.js';

const listesNewsletterDemo = structuredClone(DEMO_NEWSLETTER_LISTES);
const campagnesNewsletterDemo = structuredClone(DEMO_NEWSLETTER_CAMPAGNES);
const visuelsNewsletterDemo = structuredClone(DEMO_NEWSLETTER_VISUELS);
let numeroNewsletter = 100;
const texteHtml = (v: string) =>
  v.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  );

// M4 : copies modifiables, jamais les fixtures ni les activations.
let identitesSsoDemo = structuredClone(DEMO_IDENTITES_SSO);
const provenancesDemo = structuredClone(DEMO_PROVENANCE);
const coutsDemo = structuredClone(DEMO_COUTS);
const dossiersDemo = structuredClone(DEMO_DOSSIERS_OEUVRES);
let numeroM4 = 100;
/** Équipe de démonstration modifiable (M7) : un clone, jamais la fixture. */
const equipeDemo: { membres: MembreEquipe[]; revision: number } = {
  membres: structuredClone(DEMO_EQUIPE),
  revision: 1,
};

/**
 * Mode démonstration (S3-i, point 9 de la mission) : ouvrir `/?demo=1` fait
 * croire à la régie qu'une session existe, sans backend, pour la vérification
 * visuelle (captures d'écran) et la démonstration décideur. Le drapeau est
 * repris dans `sessionStorage` pour survivre à la navigation côté client
 * (react-router ne recharge pas la page).
 */
const CLE_DEMO = 'artosera-demo';
const CLE_DEMO_MODULES = 'artosera-demo-modules';
const CLE_DEMO_PALIERS = 'artosera-demo-paliers';

export function activerModeDemonstrationDepuisUrl(apercuPublic = false): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  if (apercuPublic || params.get('demo') === '1') {
    try {
      window.sessionStorage.setItem(CLE_DEMO, '1');
      // Une nouvelle entrée explicite remet la liste figée par défaut.
      if (params.get('demo') === '1') {
        // `modules=tous` : tout activé. `modules=lecture` : le cœur seul est
        // pleinement utilisable, tous les autres modules sont consultables en
        // lecture seule (captures du verrou, `modulesLectureSeule`).
        const choixModules = params.get('modules');
        if (choixModules === 'tous' || choixModules === 'lecture')
          window.sessionStorage.setItem(CLE_DEMO_MODULES, choixModules);
        else window.sessionStorage.removeItem(CLE_DEMO_MODULES);
        // `?demo=1&paliers=verrouilles` : V1, V2 et la version finale verrouillées.
        if (params.get('paliers') === 'verrouilles')
          window.sessionStorage.setItem(CLE_DEMO_PALIERS, 'verrouilles');
        else window.sessionStorage.removeItem(CLE_DEMO_PALIERS);
      }
    } catch {
      /* stockage indisponible : le mode démo ne survit pas à la navigation */
    }
  }
}

export function modeDemonstrationActif(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(CLE_DEMO) === '1';
  } catch {
    return false;
  }
}

function choixModulesDeDemonstration(): string | null {
  try {
    return window.sessionStorage.getItem(CLE_DEMO_MODULES);
  } catch {
    // Sans stockage accessible, garder la liste figée.
    return null;
  }
}

/**
 * Les modules DONT LES ÉCRANS répondent dans la démonstration : tous les
 * disponibles sur demande explicite (`modules=tous`, `modules=lecture`), pour
 * les captures de modules.
 */
export function modulesDeDemonstration(): readonly ModuleOptionnel[] {
  const choix = choixModulesDeDemonstration();
  if (choix === 'tous' || choix === 'lecture') return modulesDisponibles();
  return MODULES_DEMONSTRATION;
}

/**
 * Les modules en LECTURE SEULE de la démonstration (`modules=lecture`) : tous
 * les disponibles. Ils répondent en lecture et refusent l'écriture, comme
 * l'API le fait pour `modulesLectureSeule`.
 */
export function modulesLectureSeuleDeDemonstration(): readonly ModulePilotable[] {
  return choixModulesDeDemonstration() === 'lecture'
    ? [...modulesDisponibles(), ...ADDONS_DEMONSTRATION]
    : [];
}

/**
 * Les modules verrouillés de la démonstration : aucun par défaut, ceux de V1, V2
 * et de la version finale avec `?demo=1&paliers=verrouilles` (captures des
 * modules à venir). Ils sont alors fermés, comme le service des modules les
 * fermerait.
 */
export function modulesVerrouillesDeDemonstration(): readonly ModuleOptionnel[] {
  try {
    if (window.sessionStorage.getItem(CLE_DEMO_PALIERS) === 'verrouilles')
      return modulesAVenir(
        ['v1', 'v2', 'finale'].flatMap((palier) => modulesDuPalier(palier as PalierModule)),
        [],
      );
  } catch {
    // Sans stockage accessible, rien n'est verrouillé.
  }
  return [];
}

/** Les modules effectifs de la galerie de démonstration, verrous de palier compris. */
function modulesEffectifsDeDemonstration(): readonly Module[] {
  // En lecture seule, aucun module n'est effectif : seul le cœur écrit.
  return modulesDeLaGalerie(
    {
      modulesActifs: choixModulesDeDemonstration() === 'lecture' ? [] : modulesDeDemonstration(),
      droitsAddons:
        choixModulesDeDemonstration() === 'lecture'
          ? []
          : ADDONS_DEMONSTRATION.map((addon) => ({
              addon,
              mode: 'achat',
              depuisLe: '2026-01-01',
              jusquAu: null,
            })),
    },
    modulesVerrouillesDeDemonstration(),
    Date.now(),
    ADDONS_DEMONSTRATION.map((addon) => ({ addon, etat: 'publiee' })),
  );
}

const sallesDemo = structuredClone(DEMO_VIEWING_ROOMS);
const cataloguesVisiteDemo = structuredClone(DEMO_CATALOGUES_VISITE);
function modifierSalleDemo(salle: SallePresentee, corps: Partial<CorpsSalle>) {
  if (corps.titre) salle.titre = corps.titre;
  if (corps.texte) salle.texte = corps.texte;
  if (corps.oeuvres)
    salle.oeuvres = corps.oeuvres.flatMap((id) => {
      const o = DEMO_OEUVRES.find((oeuvre) => oeuvre.id === id);
      return o
        ? [
            {
              id: o.id,
              titre: o.titre,
              artisteNom: o.artisteNom,
              annee: o.annee ?? null,
              statut: o.statut,
              mediaPrincipalId: o.mediaPrincipalId,
              prixPublicCentimes: o.prixPublicCentimes ?? null,
              devise: o.devise,
            },
          ]
        : [];
    });
  if (corps.contactId !== undefined) {
    const c = DEMO_CONTACTS.find((contact) => contact.id === corps.contactId);
    salle.contact = c ? { id: c.id, nomAffiche: c.nomAffiche } : null;
  }
  if (corps.expireLe) {
    salle.expireLe = corps.expireLe;
    if (salle.etat === 'expiree') salle.etat = salle.lienEmisLe ? 'active' : 'sans_lien';
  }
  if (corps.prixVisibles !== undefined) salle.prixVisibles = corps.prixVisibles;
  if (corps.motDePasse !== undefined) salle.protegee = corps.motDePasse !== null;
  salle.modifieLe = new Date().toISOString();
}

const ROLE_DEMO = 'direction' as const;

/** Session simulée : direction de la Galerie Sainte-Avoye, palier Galerie. */
export function sessionDemo(): ReponseMoi {
  return {
    modulesVerrouilles: [...modulesVerrouillesDeDemonstration()],
    groupes: [
      {
        id: 'demo-groupe-1',
        slug: 'galeries-allen',
        nom: 'Galeries Allen',
        role: 'direction_groupe',
        galeries: ['sainte-avoye'],
      },
    ],
    compte: {
      id: 'demo-compte-1',
      email: 'direction@sainte-avoye.example',
      nom: 'Claire Vallier',
      langue: 'fr',
      rolePlateforme: null,
      deuxiemeFacteurActive: deuxiemeFacteurDemoActif(),
      graceMfaJusquau: null,
    },
    galeries: [
      {
        id: 'demo-galerie-1',
        slug: 'sainte-avoye',
        nom: 'Galerie Sainte-Avoye',
        etat: 'active',
        langue: 'fr',
        role: ROLE_DEMO,
        support: false,
        expireLe: null,
        droits: AUCUN_DROIT,
        actions: actionsPermises(sujet(ROLE_DEMO, AUCUN_DROIT)),
        palier: 'galerie',
        modules: modulesEffectifsDeDemonstration(),
        // Absent hors `modules=lecture` : la démonstration ordinaire reste
        // identique octet pour octet.
        ...(modulesLectureSeuleDeDemonstration().length > 0
          ? { modulesLectureSeule: [...modulesLectureSeuleDeDemonstration()] }
          : {}),
        // Lot C : les démonstrations commerciales gardent le changement de
        // design libre (sélecteur, liens `?design=`, captures des cinq thèmes).
        theme: 'halo',
        choixThemeClient: true,
        horsLigne: reglageHorsLigne({
          modules: modulesEffectifsDeDemonstration(),
          actions: actionsPermises(sujet(ROLE_DEMO, AUCUN_DROIT)),
          support: false,
          etat: 'active',
        }),
      },
    ],
  };
}

/** Même décompte que l'API, y compris les œuvres archivées. */
function avecNombreOeuvres(artiste: ArtistePresente): ArtistePresente {
  return {
    ...artiste,
    nombreOeuvres: DEMO_OEUVRES.filter((oeuvre) => oeuvre.artisteId === artiste.id).length,
  };
}

export const IDENTIFIANTS_DEMO: IdentifiantsEnAttente = {
  email: sessionDemo().compte.email,
  motDePasse: '',
};

const PREFIXE_GALERIE = `/v1/g/${DEMO_GALERIE}`;

/** Les alias français du coffre (`apps/api/src/coffre/commun.ts`, § 18.2). */
const ALIAS_RATTACHEMENT_DEMO: Readonly<Record<string, string>> = {
  oeuvre: 'artwork',
  artwork: 'artwork',
  artiste: 'artist',
  artist: 'artist',
  contact: 'contact',
  exposition: 'exhibition',
  exhibition: 'exhibition',
};

/** Compare un chemin d'API à un patron `/oeuvres/:id` (un seul paramètre). */
function correspond(chemin: string, patron: RegExp): RegExpMatchArray | null {
  return patron.exec(chemin);
}

/**
 * État mutable du site en mode démonstration (S6-f) : un clone profond des
 * fixtures, modifié par les PATCH/PUT/POST simulés ci-dessous, pour que
 * « changer de thème » ou « publier » se voient réellement à l'écran — sans
 * quoi la vérification visuelle en mode démo ne prouverait rien.
 */
let siteDemo: ReglagesSite = structuredClone(DEMO_SITE.site);
let pagesDemo: PageSite[] = structuredClone(DEMO_PAGES);
let publicationsDemo: PublicationPresentee[] = structuredClone(DEMO_PUBLICATIONS);
/**
 * Certificats émis en session (S4-c, § 19.5) : un acte NUMÉROTÉ, donc une
 * séquence qui avance à chaque émission simulée plutôt qu'une réponse figée
 * — sans quoi « émettre un certificat » ne se verrait jamais dans la liste.
 */
let certificatsDemo: CertificatPresente[] = structuredClone(DEMO_CERTIFICATS_INITIAUX);

function fusionnerSite(avant: ReglagesSite, corps: CorpsMiseAJourSite): ReglagesSite {
  return {
    ...avant,
    ...(corps.theme !== undefined ? { theme: corps.theme } : {}),
    charte: { ...avant.charte, ...corps.charte },
    ...(corps.accueil !== undefined ? { accueil: corps.accueil } : {}),
    ...(corps.langues !== undefined ? { langues: corps.langues } : {}),
    ...(corps.langueParDefaut !== undefined ? { langueParDefaut: corps.langueParDefaut } : {}),
    menus: {
      principal: corps.menus?.principal ?? avant.menus.principal,
      pied: corps.menus?.pied ?? avant.menus.pied,
    },
    options: { ...avant.options, ...corps.options },
    identite: { ...avant.identite, ...corps.identite },
    ...(corps.mentionArtosera !== undefined ? { mentionArtosera: corps.mentionArtosera } : {}),
    modifieLe: new Date().toISOString(),
  };
}

/** Le contrat de données courant (§ 15.9), recalculé sur `siteDemo`/`pagesDemo` :
 *  c'est ce qui rend l'aperçu réactif à un changement de thème ou de page en
 *  mode démonstration, sans backend. */
function donneesDepuisSiteDemo(): SiteData {
  const page = (rubrique: RubriquePage) => pagesDemo.find((p) => p.rubrique === rubrique);
  const laGalerie = page('la-galerie');
  const contact = page('contact');
  const mentions = page('mentions');
  const confidentialite = page('confidentialite');
  return {
    ...DEMO_SITE_DATA,
    galerie: { ...DEMO_SITE_DATA.galerie, mentionArtosera: siteDemo.mentionArtosera },
    charte: {
      ...DEMO_SITE_DATA.charte,
      theme: siteDemo.theme,
      couleurPrincipale: siteDemo.charte.couleurPrincipale,
      couleurSecondaire: siteDemo.charte.couleurSecondaire,
      policeTitres: siteDemo.charte.policeTitres,
      policeTexte: siteDemo.charte.policeTexte,
      accueil: siteDemo.accueil as SiteData['charte']['accueil'],
    },
    options: siteDemo.options,
    menus: {
      principal: siteDemo.menus.principal.map((entree) => ({
        type: 'rubrique' as const,
        rubrique: entree.rubrique,
        ...(entree.libelle ? { libelle: entree.libelle } : {}),
      })),
      pied: siteDemo.menus.pied.map((entree) => ({
        type: 'rubrique' as const,
        rubrique: entree.rubrique,
        ...(entree.libelle ? { libelle: entree.libelle } : {}),
      })),
    },
    pages: {
      laGalerie: {
        titre: laGalerie?.titre ?? DEMO_SITE_DATA.pages.laGalerie.titre,
        corps: laGalerie?.contenu ?? DEMO_SITE_DATA.pages.laGalerie.corps,
        equipe: [...(laGalerie?.equipe ?? DEMO_SITE_DATA.pages.laGalerie.equipe)],
      },
      contact: {
        titre: contact?.titre ?? DEMO_SITE_DATA.pages.contact.titre,
        corps: contact?.contenu ?? DEMO_SITE_DATA.pages.contact.corps,
        ...((contact?.appelNewsletter ?? DEMO_SITE_DATA.pages.contact.newsletter)
          ? { newsletter: contact?.appelNewsletter ?? DEMO_SITE_DATA.pages.contact.newsletter }
          : {}),
      },
      mentions: {
        titre: mentions?.titre ?? DEMO_SITE_DATA.pages.mentions.titre,
        corps: mentions?.contenu ?? DEMO_SITE_DATA.pages.mentions.corps,
      },
      confidentialite: {
        titre: confidentialite?.titre ?? DEMO_SITE_DATA.pages.confidentialite.titre,
        corps: confidentialite?.contenu ?? DEMO_SITE_DATA.pages.confidentialite.corps,
      },
    },
    redirections: siteDemo.redirections.map((redirection) => ({
      de: redirection.de,
      vers: redirection.vers,
      statut: 301 as const,
    })),
  };
}

/**
 * Simule le worker : une publication demandée passe « en cours » puis
 * « réussie » quelques secondes plus tard, et `siteDemo` avance de version —
 * de quoi voir le bouton « Publier maintenant » rafraîchir l'état sans
 * recharger la page.
 */
function publierDemo(raison: string): PublicationPresentee {
  const version = (publicationsDemo[0]?.version ?? siteDemo.versionPubliee) + 1;
  const entree: PublicationPresentee = {
    id: `publication-demo-${String(Date.now())}`,
    version,
    dossier: null,
    etat: 'en_cours',
    raison,
    debuteLe: new Date().toISOString(),
    termineLe: null,
    dureeMs: 0,
    fichiers: 0,
    medias: 0,
    polices: 0,
    octets: 0,
    hotes: [],
    erreur: null,
  };
  publicationsDemo = [entree, ...publicationsDemo];
  setTimeout(() => {
    const termineLe = new Date();
    publicationsDemo = publicationsDemo.map((publication) =>
      publication.id === entree.id
        ? {
            ...publication,
            etat: 'reussie',
            dossier: `00000${String(version)}-${termineLe.toISOString().replace(/[-:.]/g, '')}`,
            termineLe: termineLe.toISOString(),
            dureeMs: 4200,
            fichiers: 214,
            medias: 18,
            polices: 6,
            octets: 48_302_114,
            hotes: ['sainte-avoye.artosera.gallery', 'www.sainte-avoye.example'],
          }
        : publication,
    );
    siteDemo = {
      ...siteDemo,
      versionPubliee: version,
      dernierePublication: termineLe.toISOString(),
    };
  }, 4000);
  return entree;
}

const JOUR_MS_DEMO = 24 * 60 * 60 * 1000;

/**
 * Le tableau de bord de démonstration (§ 20), composé depuis les fixtures.
 *
 * Les six sections sont toutes présentes : la session de démonstration est
 * « direction » d'une galerie au palier Galerie, donc aucun module fermé ni
 * aucune action refusée.
 */
function tableauDeBordDemo(): unknown {
  const maintenant = Date.now();
  const sousSeptJours = maintenant + 7 * JOUR_MS_DEMO;
  const sousTrenteJours = maintenant + 30 * JOUR_MS_DEMO;

  const demandesATraiter = DEMO_DEMANDES.filter((demande) => demande.etat === 'a_traiter');
  const reservationsActives = DEMO_RESERVATIONS.filter(
    (reservation) => reservation.etat === 'active',
  );
  const reservationsBientot = reservationsActives
    .filter((reservation) => new Date(reservation.jusquau).getTime() <= sousSeptJours)
    .sort((a, b) => a.jusquau.localeCompare(b.jusquau));
  const expositionsVivantes = DEMO_EXPOSITIONS.filter((exposition) => !exposition.archive);
  const expositionsProchaines = expositionsVivantes
    .filter(
      (exposition) =>
        exposition.etat === 'en_cours' ||
        (exposition.etat === 'annoncee' &&
          new Date(exposition.debutLe).getTime() <= sousTrenteJours),
    )
    .sort((a, b) => a.debutLe.localeCompare(b.debutLe));

  const parStatut = Object.fromEntries(STATUTS_OEUVRE.map((statut) => [statut, 0]));
  for (const oeuvre of DEMO_OEUVRES) parStatut[oeuvre.statut] = (parStatut[oeuvre.statut] ?? 0) + 1;

  // L'œuvre en vedette (§ 20.2, audit du 26 septembre 2026) : jamais la plus
  // récente prise telle quelle — la plus récente qui soit à la fois vivante
  // et pourvue d'un média (`DEMO_MEDIAS` n'en tient un que pour les œuvres à
  // `mediaPrincipalId` non nul, comme la vraie agrégation exige au moins un
  // média avec variante). Même règle que `apps/api/src/tableau-de-bord/
  // agregation.ts`, tenue ici séparément parce que ce chemin ne touche jamais
  // le réseau.
  const oeuvreVedette =
    [...DEMO_OEUVRES]
      .filter((oeuvre) => oeuvre.statut !== 'archivee' && (DEMO_MEDIAS[oeuvre.id]?.length ?? 0) > 0)
      .sort((a, b) => b.creeLe.localeCompare(a.creeLe))[0] ?? null;

  const documentsVivants = DEMO_DOCUMENTS.filter((document) => !document.archive);
  const parType: Record<string, number> = {};
  for (const document of documentsVivants) {
    parType[document.type] = (parType[document.type] ?? 0) + 1;
  }
  const echeances = documentsVivants
    .filter((document) => document.echeanceLe !== null)
    .map((document) => ({
      id: document.id,
      titre: document.titre,
      type: document.type,
      echeanceLe: document.echeanceLe as string,
      joursRestants: joursCivilsRestants(new Date(document.echeanceLe as string)),
    }))
    .filter((echeance) => new Date(echeance.echeanceLe).getTime() <= sousTrenteJours)
    .sort((a, b) => a.joursRestants - b.joursRestants);

  const derniere = publicationsDemo[0] ?? null;

  return {
    genereLe: new Date().toISOString(),
    ...(modulesEffectifsDeDemonstration().includes('pilotage')
      ? { dormantes: { total: dormantesDemo().oeuvres.length } }
      : {}),
    demandes: {
      aTraiter: demandesATraiter.length,
      dernieres: demandesATraiter.slice(0, 5).map((demande) => ({
        id: demande.id,
        nom: demande.nom,
        sujet: demande.sujet,
        etat: demande.etat,
        oeuvreId: demande.oeuvreId,
        recueLe: demande.recueLe,
      })),
    },
    reservations: {
      actives: reservationsActives.length,
      expirentBientot: reservationsBientot.length,
      prochaines: reservationsBientot.slice(0, 5).map((reservation) => ({
        id: reservation.id,
        oeuvreId: reservation.oeuvreId,
        oeuvreTitre: DEMO_OEUVRES.find((oeuvre) => oeuvre.id === reservation.oeuvreId)?.titre ?? '',
        contactId: reservation.contactId,
        jusquau: reservation.jusquau,
      })),
    },
    expositions: {
      enCours: expositionsVivantes.filter((exposition) => exposition.etat === 'en_cours').length,
      aVenir: expositionsProchaines.filter((exposition) => exposition.etat === 'annoncee').length,
      prochaines: expositionsProchaines.slice(0, 5).map((exposition) => ({
        id: exposition.id,
        titre: exposition.titre,
        etat: exposition.etat,
        debutLe: exposition.debutLe,
        finLe: exposition.finLe,
      })),
    },
    oeuvres: {
      total: DEMO_OEUVRES.length,
      parStatut,
      dernieres: [...DEMO_OEUVRES]
        .sort((a, b) => b.creeLe.localeCompare(a.creeLe))
        .slice(0, 5)
        .map((oeuvre) => ({
          id: oeuvre.id,
          titre: oeuvre.titre,
          artisteNom: oeuvre.artisteNom,
          statut: oeuvre.statut,
          creeLe: oeuvre.creeLe,
        })),
      vedette:
        oeuvreVedette === null
          ? null
          : {
              id: oeuvreVedette.id,
              titre: oeuvreVedette.titre,
              artisteNom: oeuvreVedette.artisteNom,
              statut: oeuvreVedette.statut,
              creeLe: oeuvreVedette.creeLe,
            },
    },
    coffre: {
      enRetard: echeances.filter((echeance) => echeance.joursRestants < 0).length,
      sousTrenteJours: echeances.filter((echeance) => echeance.joursRestants >= 0).length,
      prochaines: echeances.slice(0, 5),
      total: documentsVivants.length,
      parType,
    },
    site: {
      derniere:
        derniere === null
          ? null
          : {
              version: derniere.version,
              date: derniere.termineLe ?? derniere.debuteLe,
              etat: derniere.etat,
            },
    },
  };
}

/**
 * Répond localement aux requêtes couvertes par le mode démonstration
 * (mission S3-a, point 9 : vérification visuelle sans backend). En plus de la
 * session, la galerie `sainte-avoye` sert son catalogue d'œuvres, d'artistes
 * et de lieux depuis `demoFixtures.ts` — assez pour peupler la liste, la
 * fiche ouverte et la fiche complète. `undefined` laisse passer vers le
 * réseau (aucune mutation n'est simulée : import, export et dépose de média
 * échouent normalement hors d'un vrai backend).
 */
// Copies locales à cette session de démonstration ; chaque réponse est indépendante.
const modelesDemo = new Map<string, ModeleDocument>();
function lireModeleDemo(cle: string): ModeleDocument {
  let modele = modelesDemo.get(cle);
  if (!modele) {
    modele = structuredClone({
      ...DEMO_MODELE_FACTURE,
      cle: cle as CleModele,
      nom: cle === 'facture' ? DEMO_MODELE_FACTURE.nom : cle,
      ...(cle === 'certificat'
        ? { signataire: { nom: 'Claire Vallier', qualite: 'Directrice' }, lieuEmission: 'Paris' }
        : {}),
    });
    modelesDemo.set(cle, modele);
  }
  return modele;
}

const accesAssistantDemo: JetonPresente[] = [];

export function reponseDemo(chemin: string, methode: string, corps?: unknown): unknown | undefined {
  const [chemainSansQuery, requete] = chemin.split('?');
  const pathname = chemainSansQuery ?? chemin;
  const params = new URLSearchParams(requete ?? '');

  if (pathname === '/v1/auth/me' && methode === 'GET') return sessionDemo();
  if (pathname === '/v1/auth/logout' && methode === 'POST') return null;
  if (pathname === '/v1/auth/login' && methode === 'POST') {
    const identifiants = (corps ?? {}) as { codeTotp?: string; codeSecours?: string };
    return identifiants.codeTotp || identifiants.codeSecours ? {} : { deuxiemeFacteurRequis: true };
  }
  if (pathname.startsWith('/v1/auth/2fa/')) {
    const deuxFacteurs = reponseDeuxFacteursDemo(pathname.slice('/v1/auth/2fa'.length), methode);
    if (deuxFacteurs !== undefined) return deuxFacteurs;
  }
  if (methode === 'POST') {
    if (pathname === '/v1/auth/password/forgot' || pathname === '/v1/auth/password/reset') {
      return { message: 'Démonstration : la demande de réinitialisation est simulée.' };
    }
    if (pathname === '/v1/auth/cgu/accepter') {
      const acceptation = corps as { version: string; langue: string };
      return { cgu: { ...acceptation, accepteeLe: new Date().toISOString() } };
    }
    if (pathname === '/v1/auth/cgu/refuser') return null;
    if (pathname === '/v1/auth/sso/2fa') return { retour: '/app' };
  }
  if (pathname === '/v1/archives' && methode === 'GET') return { archives: DEMO_ARCHIVES };
  // `/site/publier` est géré plus bas (S6-f) avec le suivi complet de l'état
  // (`publierDemo`) : la tuile « Votre site » du tableau de bord (S3-f) et
  // l'onglet Publication de l'écran Site partagent donc la même simulation,
  // plutôt qu'une réponse statique qui ne ferait jamais avancer l'historique.
  if (!pathname.startsWith(PREFIXE_GALERIE)) return undefined;

  const chemin_ = pathname.slice(PREFIXE_GALERIE.length);
  if (chemin_.startsWith('/assistant-reponse/') || chemin_.startsWith('/boite-reception/'))
    return reponseDemoAssistant(chemin_, params, methode, corps);
  // Studio.
  if (chemin_ === '/studio' || chemin_.startsWith('/studio/'))
    return reponseDemoStudio(chemin_, params, methode, corps);
  // Catalogage.
  if (chemin_ === '/catalogage' || chemin_.startsWith('/catalogage/'))
    return reponseDemoCatalogage(chemin_, methode);
  // Modification groupée des œuvres : aperçu et rapport calculés localement.
  if (chemin_ === '/oeuvres/lot' && methode === 'POST') return lotDemo(corps);
  if (chemin_.startsWith('/ia/')) return reponseDemoIa(chemin_, params, methode, corps);
  if (chemin_.startsWith('/assistant-recherche/'))
    return reponseDemoAssistantRecherche(chemin_, methode, corps);
  if (chemin_ === '/demande-site') return reponseDemoDemandeSite(methode, corps);
  if (chemin_.startsWith('/boutique/')) return reponseBoutiqueDemo(chemin_, params, methode, corps);
  if (chemin_ === '/comptabilite/plan' && methode === 'GET') return lirePlanDemo();
  if (chemin_ === '/comptabilite/plan' && methode === 'PUT')
    return ecrirePlanDemo(corps as PlanComptable);
  if (chemin_ === '/comptabilite/fec' && methode === 'GET')
    return apercuFecDemo(params.get('du') ?? '', params.get('au') ?? '');

  // Avant la barrière « GET seulement » plus bas : les gestes (accorder, valider,
  // signaler, retirer) sont des POST et des DELETE.
  const accesVenio = reponseAccesVenioDemo(chemin_, methode, corps);
  if (accesVenio !== undefined) return accesVenio;

  if (chemin_ === '/assistant-mcp/acces' && methode === 'GET')
    return {
      jetons: accesAssistantDemo,
      scopesAccordables: ['read:works', 'read:contacts', 'write:contacts'],
      durees: [30, 90, 180, 365],
      urlMcp: 'https://demo.artosera.fr/v1/agent/mcp',
      outils: [
        { nom: 'galerie', titre: 'Galerie', scopes: [], lecture: true },
        {
          nom: 'rechercher_oeuvres',
          titre: 'Rechercher des œuvres',
          scopes: ['read:works'],
          lecture: true,
        },
        { nom: 'lire_contact', titre: 'Lire un contact', scopes: ['read:contacts'], lecture: true },
        {
          nom: 'creer_contact',
          titre: 'Créer un contact',
          scopes: ['write:contacts'],
          lecture: false,
        },
      ],
    } satisfies ReponseAccesAssistant;
  if (chemin_ === '/assistant-mcp/acces' && methode === 'POST') {
    const c = corps as CorpsJeton;
    const jeton: JetonPresente = {
      id: `assistant-demo-${Date.now()}`,
      nom: c.nom,
      prefixe: 'arto_pat_demo',
      scopes: c.scopes,
      etat: 'ACTIF',
      expireLe: new Date(Date.now() + c.expireDansJours * 86400000).toISOString(),
      creeLe: new Date().toISOString(),
      creePar: 'direction@sainte-avoye.example',
      dernierUsageLe: null,
      revoqueLe: null,
    };
    accesAssistantDemo.unshift(jeton);
    return { jeton, secret: 'arto_pat_DEMONSTRATION01234567890123456789' };
  }
  const revocationAssistant = /^\/assistant-mcp\/acces\/([^/]+)\/revoquer$/.exec(chemin_);
  if (revocationAssistant && methode === 'POST') {
    const i = accesAssistantDemo.findIndex((j) => j.id === revocationAssistant[1]);
    const lu = accesAssistantDemo[i];
    if (lu) {
      const jeton = { ...lu, etat: 'REVOQUE' as const, revoqueLe: new Date().toISOString() };
      accesAssistantDemo[i] = jeton;
      return { jeton };
    }
  }

  if (chemin_ === '/acces-api/jetons' && methode === 'GET')
    return {
      jetons: DEMO_JETONS,
      scopesAccordables: [
        'read:works',
        'write:works',
        'read:accounting',
        'write:accounting',
        'read:invoices',
      ],
      durees: [30, 90, 180, 365],
    };
  if (chemin_ === '/acces-api/jetons' && methode === 'POST') {
    const c = corps as CorpsJeton;
    const jeton: JetonPresente = {
      id: `jeton-demo-${Date.now()}`,
      nom: c.nom,
      prefixe: 'arto_pat_demo',
      scopes: c.scopes,
      etat: 'ACTIF',
      expireLe: new Date(Date.now() + c.expireDansJours * 86400000).toISOString(),
      creeLe: new Date().toISOString(),
      creePar: 'direction@sainte-avoye.example',
      dernierUsageLe: null,
      revoqueLe: null,
    };
    DEMO_JETONS.unshift(jeton);
    return { jeton, secret: 'arto_pat_DEMONSTRATION01234567890123456789' };
  }
  const revocationJeton = /^\/acces-api\/jetons\/([^/]+)\/revoquer$/.exec(chemin_);
  if (revocationJeton && methode === 'POST') {
    const i = DEMO_JETONS.findIndex((j) => j.id === revocationJeton[1]);
    const lu = DEMO_JETONS[i];
    if (lu) {
      const jeton = { ...lu, etat: 'REVOQUE' as const, revoqueLe: new Date().toISOString() };
      DEMO_JETONS[i] = jeton;
      return { jeton };
    }
  }

  const presentation = reponseDemoPresentation(chemin_, params, methode, corps);
  if (presentation !== undefined) return presentation;
  const diffusion = reponseDemoDiffusion(chemin_, params, methode, corps);
  if (diffusion !== undefined) return diffusion;
  const contrats = reponseDemoContrats(chemin_, params, methode, corps);
  if (contrats !== undefined) return contrats;
  const pilotage = reponseDemoPilotage(chemin_, params, methode, corps);
  if (pilotage !== undefined) return pilotage;

  // Agenda et presse (M6).
  if (chemin_ === '/newsletter/listes') {
    if (methode === 'POST') {
      const valeurs = corps as CorpsListe;
      const liste = {
        ...structuredClone(DEMO_NEWSLETTER_LISTES[0]!),
        ...valeurs,
        id: `liste-${String(++numeroNewsletter)}`,
        membres: 0,
        synchronisee: false,
      };
      listesNewsletterDemo.push(liste);
      return { liste };
    }
    return { listes: listesNewsletterDemo.filter((l) => !l.archive) };
  }
  const archiveListe = /^\/newsletter\/listes\/([^/]+)\/archiver$/.exec(chemin_);
  if (archiveListe && methode === 'POST') {
    const liste = listesNewsletterDemo.find((l) => l.id === archiveListe[1]);
    if (liste) {
      liste.archive = true;
      return { liste };
    }
  }
  if (chemin_ === '/newsletter/campagnes') {
    if (methode === 'POST') {
      const campagne = {
        ...structuredClone(DEMO_NEWSLETTER_CAMPAGNES[1]!),
        ...(corps as CorpsCampagne),
        id: `campagne-${String(++numeroNewsletter)}`,
        blocs: [],
        listes: [],
      };
      campagnesNewsletterDemo.push(campagne);
      return { campagne };
    }
    return {
      campagnes: campagnesNewsletterDemo,
      pagination: { page: 1, parPage: 50, total: campagnesNewsletterDemo.length, pages: 1 },
    };
  }
  const campagneNewsletter =
    /^\/newsletter\/campagnes\/([^/]+)(?:\/(apercu|envoyer|statistiques))?$/.exec(chemin_);
  if (campagneNewsletter) {
    const campagne = campagnesNewsletterDemo.find((c) => c.id === campagneNewsletter[1]);
    if (!campagne) return undefined;
    if (campagneNewsletter[2] === 'apercu')
      return {
        html: `<html><body><h1>${texteHtml(campagne.sujet)}</h1>${campagne.blocs.map((b) => (b.type === 'texte' ? `<p>${texteHtml(b.texte)}</p>` : `<p>${texteHtml(b.type === 'oeuvre' ? (DEMO_OEUVRES.find((o) => o.id === b.oeuvreId)?.titre ?? 'Œuvre') : (DEMO_EXPOSITIONS.find((e) => e.id === b.expositionId)?.titre.fr ?? 'Exposition'))}</p>`)).join('')}</body></html>`,
      };
    if (campagneNewsletter[2] === 'envoyer') {
      campagne.etat = 'envoyee';
      campagne.envoyeeLe = new Date().toISOString();
      return { campagne };
    }
    if (methode === 'PATCH') {
      Object.assign(campagne, corps, { revision: campagne.revision + 1 });
    }
    return { campagne };
  }
  if (chemin_ === '/newsletter/visuels') {
    if (methode === 'POST') {
      const visuel = {
        ...structuredClone(DEMO_NEWSLETTER_VISUELS[0]!),
        ...(corps as Pick<VisuelNewsletter, 'cible' | 'format'>),
        id: `visuel-${String(++numeroNewsletter)}`,
      };
      visuelsNewsletterDemo.push(visuel);
      return { visuel };
    }
    return { visuels: visuelsNewsletterDemo };
  }
  if (/^\/newsletter\/visuels\/[^/]+\/telechargement$/.test(chemin_))
    return {
      telechargement: {
        url: /visuel-2/.test(chemin_) ? pngNewsletterVertical : pngNewsletterCarre,
        expireLe: new Date(Date.now() + 300_000).toISOString(),
      },
    };
  if (chemin_ === '/newsletter/reglages') return { reglages: DEMO_NEWSLETTER_REGLAGES };
  if (chemin_ === '/newsletter/synchroniser' || chemin_ === '/newsletter/webhook')
    return { travail: { id: 'demo-newsletter', etat: 'reussi' } };

  if (chemin_ === '/agenda' && methode === 'GET') {
    const ids = params.get('sources')?.split(',');
    const du = params.get('du') ?? '';
    const au = params.get('au') ?? '9999';
    return {
      evenements: DEMO_AGENDA.filter(
        (e) =>
          e.debut.slice(0, 10) <= au &&
          (e.fin ?? e.debut).slice(0, 10) >= du &&
          (!ids || ids.includes(e.source)),
      ),
      sources: ['expositions', 'rendez-vous', 'coffre'],
    };
  }
  const rendezVousAgenda = correspond(chemin_, /^\/agenda\/rendez-vous\/([^/]+)$/);
  if (rendezVousAgenda && methode === 'GET')
    return {
      rendezVous: DEMO_RENDEZ_VOUS.find((r) => r.id === rendezVousAgenda[1]) ?? DEMO_RENDEZ_VOUS[0],
    };
  if (chemin_ === '/agenda/ical' && methode === 'GET')
    return { actif: false, creeLe: null, dernierAccesLe: null };

  if (chemin_ === '/presse' && methode === 'GET') {
    const articles = DEMO_PRESSE.filter(
      (a) =>
        (!params.get('genre') || a.genre === params.get('genre')) &&
        (params.get('archives') !== 'true' ? !a.archiveLe : !!a.archiveLe) &&
        (!params.get('expositionId') || a.expositions.includes(params.get('expositionId')!)) &&
        (!params.get('artisteId') || a.artistes.includes(params.get('artisteId')!)) &&
        a.titre.toLocaleLowerCase().includes((params.get('q') ?? '').toLocaleLowerCase()),
    );
    return { articles, pagination: { page: 1, parPage: 30, total: articles.length, pages: 1 } };
  }
  const fichePresse = correspond(chemin_, /^\/presse\/([^/]+)$/);
  if (fichePresse && methode === 'GET')
    return { article: DEMO_PRESSE.find((a) => a.id === fichePresse[1]) ?? DEMO_PRESSE[0] };
  if (/^\/presse\/[^/]+\/fichiers\/\d+\/telechargement$/.test(chemin_) && methode === 'GET')
    return { url: DEMO_PDF_DOCUMENT_DATA_URI, nomFichier: 'Coupure.pdf', expireDansSecondes: 300 };

  // ── Provenance, coûts et dossiers d’œuvres (M4) ────────────────────────
  const provenanceM4 = /^\/oeuvres\/([^/]+)\/provenance(?:\/([^/]+))?$/.exec(chemin_);
  if (provenanceM4?.[1]) {
    const id = provenanceM4[1],
      cible = provenanceM4[2];
    const entrees = provenancesDemo[id] ?? (provenancesDemo[id] = []);
    const d = (corps ?? {}) as CorpsProvenance;
    if (methode === 'PUT' && cible === 'ordre') {
      const ids = (corps as { ids: string[] }).ids;
      entrees.sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id));
      entrees.forEach((p, i) => (p.rang = i));
      return { entrees };
    }
    if (methode === 'POST') {
      const entree = {
        id: `provenance-${++numeroM4}`,
        oeuvreId: id,
        exemplaireId: d.exemplaireId ?? null,
        rang: entrees.length,
        type: d.type,
        libelle: d.libelle,
        contactId: d.contactId ?? null,
        contactNom: DEMO_CONTACTS.find((c) => c.id === d.contactId)?.nomAffiche ?? null,
        periode: d.periode ?? '',
        mode: d.mode ?? null,
        source: d.source ?? '',
        public: d.public ?? false,
        note: d.note ?? '',
        ligne: d.libelle,
      };
      entrees.push(entree);
      return { entree };
    }
    if (methode === 'PATCH' && cible) {
      const entree = entrees.find((p) => p.id === cible);
      if (!entree) return undefined;
      Object.assign(entree, d);
      entree.contactNom = DEMO_CONTACTS.find((c) => c.id === entree.contactId)?.nomAffiche ?? null;
      return { entree };
    }
    if (methode === 'DELETE' && cible) {
      provenancesDemo[id] = entrees.filter((p) => p.id !== cible);
      return {};
    }
    return {
      entrees,
      lignesPubliques: entrees
        .filter((p) => p.public)
        .map((p) => (p.periode ? `${p.libelle}, ${p.periode}` : p.libelle)),
      ventesConnues:
        id === 'oeuvre-1'
          ? [
              {
                origine: 'vente_simple',
                contactId: 'contact-1',
                contactNom: DEMO_CONTACTS[0]?.nomAffiche ?? 'Collectionneur',
                date: '2026-09-01T00:00:00Z',
                reference: null,
              },
            ]
          : [],
    };
  }
  const coutM4 = /^\/oeuvres\/([^/]+)\/couts(?:\/([^/]+))?$/.exec(chemin_);
  if (coutM4?.[1]) {
    const id = coutM4[1],
      cible = coutM4[2],
      couts = coutsDemo[id] ?? (coutsDemo[id] = []),
      d = (corps ?? {}) as CorpsCout;
    if (methode === 'POST') {
      const cout = {
        id: `cout-${++numeroM4}`,
        exemplaireId: d.exemplaireId ?? null,
        categorie: d.categorie,
        libelle: d.libelle,
        montantCentimes: d.montantCentimes,
        devise: d.devise ?? 'EUR',
        date: d.date ?? null,
        fournisseur: d.fournisseur ?? '',
        note: d.note ?? '',
      };
      couts.push(cout);
      return { cout };
    }
    if (methode === 'PATCH' && cible) {
      const cout = couts.find((c) => c.id === cible);
      if (!cout) return undefined;
      Object.assign(cout, d);
      return { cout };
    }
    if (methode === 'DELETE' && cible) {
      coutsDemo[id] = couts.filter((c) => c.id !== cible);
      return {};
    }
    const totaux = new Map<string, number>();
    for (const c of couts) totaux.set(c.devise, (totaux.get(c.devise) ?? 0) + c.montantCentimes);
    return {
      couts,
      totaux: [...totaux].map(([devise, totalCentimes]) => ({ devise, totalCentimes })),
    };
  }
  const dossierM4 = /^\/dossiers-oeuvres(?:\/([^/]+))?(?:\/(pdf|archiver))?$/.exec(chemin_);
  if (dossierM4) {
    const id = dossierM4[1],
      action = dossierM4[2],
      d = (corps ?? {}) as CorpsDossierOeuvres;
    if (methode === 'POST' && !id) {
      const dossier = {
        id: `dossier-${++numeroM4}`,
        titre: d.titre,
        contactId: d.contactId ?? null,
        oeuvres: [...d.oeuvres],
        message: d.message ?? '',
        langue: d.langue ?? 'fr',
        avecPrix: d.avecPrix ?? false,
        avecProvenance: d.avecProvenance ?? false,
        archiveLe: null,
        creeLe: new Date().toISOString(),
        modifieLe: new Date().toISOString(),
      };
      dossiersDemo.push(dossier);
      return { dossier };
    }
    if (!id)
      return {
        dossiers: dossiersDemo.filter(
          (p) =>
            (params.get('archives') === 'true' ? p.archiveLe !== null : p.archiveLe === null) &&
            (!params.get('contactId') || p.contactId === params.get('contactId')),
        ),
      };
    const dossier = dossiersDemo.find((p) => p.id === id);
    if (!dossier) return undefined;
    if (action === 'pdf')
      return {
        url: DEMO_PDF_DOCUMENT_DATA_URI,
        expireLe: new Date(Date.now() + 300000).toISOString(),
        expireDansSecondes: 300,
        nomFichier: 'dossier.pdf',
      };
    if (action === 'archiver' && methode === 'POST') dossier.archiveLe = new Date().toISOString();
    if (methode === 'PATCH') Object.assign(dossier, d, { modifieLe: new Date().toISOString() });
    return { dossier };
  }

  if (chemin_ === '/viewing-rooms' && methode === 'POST') {
    const salle: SallePresentee = {
      ...structuredClone(DEMO_VIEWING_ROOMS[0]!),
      id: `salle-demo-${crypto.randomUUID()}`,
      etat: 'sans_lien',
      contact: null,
      protegee: false,
      prixVisibles: false,
      hoteLien: null,
      lienEmisLe: null,
      revoqueeLe: null,
      expireLe: new Date(Date.now() + 14 * 86_400_000).toISOString(),
      creeLe: new Date().toISOString(),
      ouvertures: { total: 0, visiteurs: 0, premiereLe: null, derniereLe: null, journal: [] },
    };
    modifierSalleDemo(salle, (corps ?? {}) as CorpsSalle);
    sallesDemo.unshift(salle);
    return structuredClone(salle);
  }
  if (chemin_ === '/viewing-rooms' && methode === 'GET') {
    const salles = sallesDemo.filter(
      (s) =>
        (!params.get('etat') || s.etat === params.get('etat')) &&
        (!params.get('contactId') || s.contact?.id === params.get('contactId')) &&
        (!params.get('oeuvreId') || s.oeuvres.some((o) => o.id === params.get('oeuvreId'))),
    );
    const page = Math.max(1, Number(params.get('page') ?? 1));
    const parPage = Math.max(1, Number(params.get('parPage') ?? 50));
    return {
      salles: structuredClone(salles.slice((page - 1) * parPage, page * parPage)),
      pagination: {
        page,
        parPage,
        total: salles.length,
        pages: Math.ceil(salles.length / parPage),
      },
    };
  }
  const salleRoute = /^\/viewing-rooms\/([^/]+)(?:\/(lien|revoquer))?$/.exec(chemin_);
  if (salleRoute) {
    const salle = sallesDemo.find((s) => s.id === salleRoute[1]);
    if (!salle) return undefined;
    if (methode === 'GET' && !salleRoute[2]) return structuredClone(salle);
    if (methode === 'PATCH' && !salleRoute[2]) {
      modifierSalleDemo(salle, (corps ?? {}) as Partial<CorpsSalle>);
      return structuredClone(salle);
    }
    if (methode === 'POST' && salleRoute[2] === 'revoquer') {
      salle.etat = 'revoquee';
      salle.revoqueeLe = new Date().toISOString();
      return structuredClone(salle);
    }
    if (methode === 'POST' && salleRoute[2] === 'lien') {
      salle.etat = 'active';
      salle.revoqueeLe = null;
      salle.lienEmisLe = new Date().toISOString();
      salle.hoteLien = 'sainte-avoye.artosera.gallery';
      const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
      const alea = crypto.getRandomValues(new Uint8Array(39));
      return {
        salle: structuredClone(salle),
        lien: {
          url: `https://${salle.hoteLien}/vr/demo${Array.from(alea, (n) => alphabet[n % 64]).join('')}`,
          expireLe: salle.expireLe,
        },
      };
    }
  }

  // ── Tableau de bord agrégé (§ 20, S3-g) ──────────────────────────────────
  // Composé DEPUIS les mêmes fixtures que les listes : le mode démonstration
  // ne doit pas raconter autre chose que ce que les écrans montrent.
  if (chemin_ === '/groupe' && methode === 'GET')
    return { ...DEMO_CONSOLE_GROUPE, depuis: params.get('depuis') ?? DEMO_CONSOLE_GROUPE.depuis };
  if (chemin_ === '/tableau-de-bord' && methode === 'GET') return tableauDeBordDemo();

  // Site (S6-f) : seul module dont le mode démonstration simule aussi les
  // mutations — sans quoi « changer de thème » ou « publier » ne se
  // verraient jamais à l'écran, alors que le point 6 de la mission demande
  // une vérification visuelle réelle en mode démo.
  if (chemin_ === '/site' && methode === 'GET') {
    return { site: siteDemo, domaine: DEMO_SITE.domaine };
  }
  if (chemin_ === '/site' && methode === 'PATCH') {
    siteDemo = fusionnerSite(siteDemo, (corps ?? {}) as CorpsMiseAJourSite);
    return { site: siteDemo };
  }
  if (chemin_ === '/site/redirections' && methode === 'PUT') {
    const envoyees =
      ((corps ?? {}) as { redirections?: readonly CorpsRedirection[] }).redirections ?? [];
    const anciennes = new Map(siteDemo.redirections.map((r) => [r.de, r]));
    siteDemo = {
      ...siteDemo,
      redirections: envoyees.map((r) => ({
        de: r.de,
        vers: r.vers,
        code: r.code ?? 301,
        poseeLe: anciennes.get(r.de)?.poseeLe ?? new Date().toISOString(),
      })),
    };
    return { redirections: siteDemo.redirections };
  }
  if (chemin_ === '/site/pages' && methode === 'GET') {
    return { pages: pagesDemo };
  }
  if (chemin_ === '/site/donnees' && methode === 'GET') {
    return { donnees: donneesDepuisSiteDemo(), omissions: [] };
  }
  if (chemin_ === '/site/publications' && methode === 'GET') {
    const limite = Number(params.get('limite') ?? '20');
    return { publications: publicationsDemo.slice(0, Math.min(Math.max(limite, 1), 100)) };
  }
  if (chemin_ === '/site/publier' && methode === 'POST') {
    const raison = ((corps ?? {}) as { raison?: string }).raison ?? 'publication demandée';
    const publication = publierDemo(raison);
    return {
      travail: {
        id: publication.id,
        type: 'site:publish',
        etat: 'en_attente',
        passeLe: publication.debuteLe,
        regroupe: false,
      },
    };
  }
  const pageRubrique = correspond(chemin_, /^\/site\/pages\/([^/]+)$/);
  if (pageRubrique?.[1]) {
    const rubrique = pageRubrique[1] as RubriquePage;
    const page = pagesDemo.find((p) => p.rubrique === rubrique);
    if (!page) return undefined;
    if (methode === 'GET') return { page };
    if (methode === 'PATCH') {
      const modifications = (corps ?? {}) as CorpsMiseAJourPage;
      const apres: PageSite = {
        ...page,
        ...(modifications.titre !== undefined ? { titre: modifications.titre } : {}),
        ...(modifications.contenu !== undefined ? { contenu: modifications.contenu } : {}),
        ...(modifications.equipe !== undefined ? { equipe: modifications.equipe } : {}),
        ...(modifications.appelNewsletter !== undefined
          ? { appelNewsletter: modifications.appelNewsletter }
          : {}),
        ...(modifications.visibleSurLeSite !== undefined
          ? { visibleSurLeSite: modifications.visibleSurLeSite }
          : {}),
        modifieLe: new Date().toISOString(),
      };
      pagesDemo = pagesDemo.map((p) => (p.rubrique === rubrique ? apres : p));
      return { page: apres };
    }
  }
  if (chemin_ === '/medias' && methode === 'GET' && !params.has('rattachementType')) {
    return {
      medias: DEMO_MEDIAS_GALERIE,
      pagination: { page: 1, parPage: 200, total: DEMO_MEDIAS_GALERIE.length, pages: 1 },
    };
  }

  // ── Émission d'un certificat (S4-c, § 19.5) ─────────────────────────────
  // Simulée comme un ACTE, pas un simple POST : un numéro pris à la
  // séquence (ici, la longueur du tableau), le cartel figé, la liste
  // invalidée par la mutation qui suit. Avant la coupure `GET` ci-dessous,
  // comme `/site/publier`.
  const annulationCertificat = correspond(
    chemin_,
    /^\/oeuvres\/([^/]+)\/certificats\/([^/]+)\/annulation$/,
  );
  if (annulationCertificat && methode === 'POST') {
    const certificat = certificatsDemo.find(
      (acte) => acte.id === annulationCertificat[2] && acte.oeuvreId === annulationCertificat[1],
    );
    if (!certificat) throw new ApiError(404, 'ressource.introuvable', 'Certificat introuvable.');
    if (certificat.annuleLe)
      throw new ApiError(409, 'conflit.version', 'Ce certificat est déjà annulé.');
    const demande = annulationCertificatSchema.parse(corps);
    const apres = {
      ...certificat,
      annuleLe: new Date().toISOString(),
      motifPublic: demande.motifPublic,
    };
    certificatsDemo = certificatsDemo.map((acte) => (acte.id === apres.id ? apres : acte));
    return { certificat: apres };
  }
  const emissionCertificat = correspond(chemin_, /^\/oeuvres\/([^/]+)\/certificat$/);
  if (emissionCertificat?.[1] && methode === 'POST') {
    const oeuvre = DEMO_OEUVRES.find((candidate) => candidate.id === emissionCertificat[1]);
    if (!oeuvre) return undefined;
    const demande = (corps ?? {}) as CorpsEmissionCertificat;
    const emisLe = new Date();
    const numero = `C-${String(emisLe.getFullYear())}-${String(certificatsDemo.length + 1).padStart(4, '0')}`;
    const certificat: CertificatPresente = {
      id: `certificat-${String(certificatsDemo.length + 1)}`,
      numero,
      oeuvreId: oeuvre.id,
      adresseVerification: 'https://demonstration.invalid/verifier/00000000000000000000000000',
      annuleLe: null,
      motifPublic: null,
      exemplaireId: demande.exemplaireId ?? null,
      exemplaire:
        demande.exemplaire ?? (oeuvre.tirage ? `Tirage de ${String(oeuvre.tirage.taille)}` : null),
      langue: demande.langue ?? 'fr',
      emisLe: emisLe.toISOString(),
      cartel: {
        artiste: oeuvre.artisteNom,
        titre: oeuvre.titre,
        ...(oeuvre.annee !== null ? { annee: String(oeuvre.annee) } : {}),
        ...(oeuvre.technique?.fr ? { technique: oeuvre.technique.fr } : {}),
        numeroInventaire: oeuvre.numeroInventaire,
      },
      provenance:
        demande.provenance ??
        (modulesDeDemonstration().includes('editions-provenance')
          ? (provenancesDemo[oeuvre.id] ?? [])
              .filter((p) => p.public)
              .map((p) => (p.periode ? `${p.libelle}, ${p.periode}` : p.libelle))
          : []),
      signataire: { nom: 'Claire Vallier', qualite: 'Directrice' },
      lieuEmission: demande.lieuEmission ?? 'Paris',
      documentPret: true,
    };
    certificatsDemo = [certificat, ...certificatsDemo];
    return {
      certificat,
      document: {
        url: DEMO_PDF_DOCUMENT_DATA_URI,
        expireLe: new Date(Date.now() + 300_000).toISOString(),
        expireDansSecondes: 300,
        nomFichier: `certificat-${numero}.pdf`,
      },
    };
  }

  const modificationMembre = correspond(chemin_, /^\/equipe\/([^/]+)\/(role|droits)$/);
  if (modificationMembre?.[1] && methode === 'PUT') {
    const cible = equipeDemo.membres.find((membre) => membre.compteId === modificationMembre[1]);
    if (!cible) return undefined;
    const changement = corps as { role?: MembreEquipe['role']; droits?: MembreEquipe['droits'] };
    const modifie: MembreEquipe =
      modificationMembre[2] === 'role' && changement.role !== undefined
        ? { ...cible, role: changement.role, droits: { ...AUCUN_DROIT } }
        : { ...cible, droits: { ...cible.droits, ...(changement.droits ?? {}) } };
    equipeDemo.membres = equipeDemo.membres.map((membre) =>
      membre.compteId === modifie.compteId ? modifie : membre,
    );
    equipeDemo.revision += 1;
    return { membre: modifie, revision: equipeDemo.revision };
  }

  const gesteOffre = mutationOffresDemo(chemin_, methode, corps);
  if (gesteOffre !== undefined) return gesteOffre;
  const identiteSso = correspond(chemin_, /^\/sso\/identites\/([^/]+)$/);
  if (identiteSso?.[1] && methode === 'DELETE') {
    const identite = identitesSsoDemo.find((i) => i.id === identiteSso[1]);
    if (!identite) return undefined;
    identitesSsoDemo = identitesSsoDemo.filter((i) => i.id !== identite.id);
    return { identite };
  }

  // Simulation d’une demande : aucune file réelle ni PDF terminé en démo.
  const preparationExposition = correspond(
    chemin_,
    /^\/expositions\/([^/]+)\/documents\/preparer$/,
  );
  if (preparationExposition?.[1] && methode === 'POST') {
    const exposition = DEMO_EXPOSITIONS.find(
      (candidate) => candidate.id === preparationExposition[1],
    );
    if (!exposition) throw new ApiError(404, 'ressource.introuvable', 'Introuvable.');
    const presentes = new Set(DEMO_OEUVRES.map((oeuvre) => oeuvre.id));
    const total = 1 + new Set(exposition.oeuvres.filter((id) => presentes.has(id))).size;
    return { total, enfiles: total };
  }

  if (chemin_ === '/ventes/modeles' && methode === 'GET') {
    lireModeleDemo('facture');
    return { modeles: structuredClone([...modelesDemo.values()]) };
  }
  const modeleVente = correspond(chemin_, /^\/ventes\/modeles\/([^/]+)$/);
  if (modeleVente?.[1] !== undefined) {
    const cle = modeleVente[1];
    const avant = lireModeleDemo(cle);
    if (methode === 'PATCH') {
      const mise = Object.fromEntries(
        Object.entries((corps ?? {}) as CorpsMiseAJourModele).filter(
          ([, valeur]) => valeur !== undefined,
        ),
      ) as CorpsMiseAJourModele;
      modelesDemo.set(
        cle,
        structuredClone({
          ...avant,
          ...mise,
          couleurs: { ...avant.couleurs, ...mise.couleurs },
          modifieLe: new Date(Math.max(Date.now(), Date.parse(avant.modifieLe) + 1)).toISOString(),
        }),
      );
    } else if (methode !== 'GET') return undefined;
    return { modele: structuredClone(lireModeleDemo(cle)) };
  }

  const routeCatalogue =
    /^\/expositions\/([^/]+)\/catalogue-visite(?:\/(regenerer|statistiques))?$/.exec(chemin_);
  if (routeCatalogue) {
    const catalogue = cataloguesVisiteDemo.find(
      (candidate) => candidate.exposition.id === routeCatalogue[1],
    );
    if (!catalogue) return undefined;
    if (routeCatalogue[2] === 'statistiques' && methode === 'GET') {
      const jusqua = new Date().toISOString().slice(0, 10);
      const depuis = new Date(Date.now() - 29 * 86_400_000).toISOString().slice(0, 10);
      const oeuvres = catalogue.oeuvres.map((oeuvre, rang) => ({
        oeuvreId: oeuvre.oeuvreId,
        ouvertures: 24 - rang,
        demandes: rang + 1,
      }));
      return {
        depuis,
        jusqua,
        ouverturesParJour: [
          {
            jour: jusqua,
            accueil: 12,
            oeuvres: oeuvres.reduce((total, oeuvre) => total + oeuvre.ouvertures, 0),
            total: 12 + oeuvres.reduce((total, oeuvre) => total + oeuvre.ouvertures, 0),
          },
        ],
        oeuvres,
        topOeuvres: oeuvres.slice(0, 10),
      };
    }
    if (methode === 'PUT' && !routeCatalogue[2]) {
      const changements = corps as CatalogueVisiteMiseAJour;
      if (changements.revision !== catalogue.revision)
        throw new ApiError(
          409,
          'conflit.version',
          'Le catalogue a été modifié. Rechargez-le avant de recommencer.',
        );
      Object.assign(catalogue, {
        ...changements,
        oeuvres: catalogue.oeuvres.map((oeuvre) => ({
          ...oeuvre,
          ...changements.oeuvres.find((entree) => entree.oeuvreId === oeuvre.oeuvreId),
        })),
        revision: catalogue.revision + 1,
      });
    }
    if (methode === 'POST' && routeCatalogue[2] === 'regenerer') {
      catalogue.jeton = crypto.randomUUID().replaceAll('-', '').padEnd(43, '_');
      catalogue.url = `https://sainte-avoye.artosera.gallery/visite/${catalogue.jeton}`;
      catalogue.oeuvres.forEach((oeuvre) => {
        oeuvre.jeton = crypto.randomUUID().replaceAll('-', '').padEnd(43, '_');
        oeuvre.url = `${catalogue.url}/${oeuvre.jeton}`;
      });
      catalogue.regenereLe = new Date().toISOString();
      catalogue.revision += 1;
    }
    catalogue.etat = etatCatalogue(
      {
        ...catalogue,
        visibleJusquAu: catalogue.visibleJusquAu ? new Date(catalogue.visibleJusquAu) : null,
      },
      new Date(),
    );
    catalogue.texteEffectif = {
      fr:
        catalogue.texte.fr.trim() ||
        DEMO_EXPOSITIONS.find((exposition) => exposition.id === catalogue.exposition.id)?.texte
          ?.fr ||
        '',
      en:
        catalogue.texte.en?.trim() ||
        DEMO_EXPOSITIONS.find((exposition) => exposition.id === catalogue.exposition.id)?.texte
          ?.en ||
        '',
    };
    for (const oeuvre of catalogue.oeuvres) {
      const originale = DEMO_OEUVRES.find((candidate) => candidate.id === oeuvre.oeuvreId)!;
      oeuvre.prixAffiche = prixAffiche({
        prixCatalogue: catalogue.prix,
        prixOeuvreCatalogue: oeuvre.prix,
        prixVisibleOeuvre: originale.prixVisible,
        prixPublicCentimes: originale.prixPublicCentimes,
      });
      oeuvre.prixPublicCentimes = oeuvre.prixAffiche ? originale.prixPublicCentimes : null;
      oeuvre.servie =
        !oeuvre.masquee && oeuvre.statut !== 'archivee' && oeuvre.statut !== 'retiree';
    }
    catalogue.nombreOeuvresServies = catalogue.oeuvres.filter((oeuvre) => oeuvre.servie).length;
    return structuredClone(catalogue);
  }

  if (methode !== 'GET') return undefined;

  if (chemin_ === '/offres') {
    const jour = new Date().toISOString().slice(0, 10);
    const due = (offre: (typeof offresDemo)[number]) =>
      offre.etat === 'envoyee' && offre.relancePrevueLe !== null && offre.relancePrevueLe <= jour;
    const offres = offresDemo.filter(
      (offre) =>
        (!params.has('etat') || offre.etat === params.get('etat')) &&
        (params.get('closes') !== 'true' ||
          ['refusee', 'expiree', 'retiree'].includes(offre.etat)) &&
        (params.get('aRelancer') !== 'true' || due(offre)) &&
        (!params.has('contactId') || offre.contactId === params.get('contactId')) &&
        (!params.has('oeuvreId') ||
          offre.lignes.some((l) => l.oeuvreId === params.get('oeuvreId'))),
    );
    const page = Number(params.get('page') ?? 1);
    const parPage = Number(params.get('parPage') ?? 50);
    return {
      offres: offres.slice((page - 1) * parPage, page * parPage),
      compteurs: {
        brouillon: offresDemo.filter((o) => o.etat === 'brouillon').length,
        envoyee: offresDemo.filter((o) => o.etat === 'envoyee').length,
        aRelancer: offresDemo.filter(due).length,
      },
      pagination: {
        page,
        parPage,
        total: offres.length,
        pages: Math.max(1, Math.ceil(offres.length / parPage)),
      },
    };
  }
  const historiqueOffres = /^\/offres\/contacts\/([^/]+)\/historique$/.exec(chemin_);
  if (historiqueOffres) {
    const id = historiqueOffres[1];
    return {
      achats: {
        factures: DEMO_FACTURES.filter(
          (f) => f.contactId === id && f.type === 'facture' && ['emise', 'payee'].includes(f.etat),
        ).map((f) => ({
          id: f.id,
          numero: f.numero,
          emiseLe: f.emiseLe,
          totalTtcCentimes: f.totaux?.totalTtcCentimes ?? null,
          devise: f.devise,
          oeuvres: f.lignes.flatMap((l) =>
            l.oeuvreId ? [{ id: l.oeuvreId, titre: l.cartel?.titre ?? l.designation }] : [],
          ),
        })),
        ventesSimples: [],
      },
      interets: [
        ...offresDemo
          .filter((o) => o.contactId === id)
          .map((o) => ({
            type: 'offre',
            id: o.id,
            numero: o.numero,
            oeuvres: o.lignes.map((l) => ({ id: l.oeuvreId, titre: l.cartel.titre })),
            date: o.creeeLe,
            etat: o.etat,
          })),
        ...DEMO_RESERVATIONS.filter((r) => r.contactId === id).map((r) => ({
          type: 'reservation',
          id: r.id,
          oeuvres: [
            { id: r.oeuvreId, titre: DEMO_OEUVRES.find((o) => o.id === r.oeuvreId)?.titre ?? '' },
          ],
          date: r.creeeLe,
          etat: r.etat,
        })),
        ...DEMO_DEMANDES.filter((d) => d.contactId === id).map((d) => ({
          type: 'demande',
          id: d.id,
          oeuvres: d.oeuvreId
            ? [
                {
                  id: d.oeuvreId,
                  titre: DEMO_OEUVRES.find((o) => o.id === d.oeuvreId)?.titre ?? '',
                },
              ]
            : [],
          date: d.recueLe,
          etat: d.etat,
        })),
      ].sort((a, b) => b.date.localeCompare(a.date)),
    };
  }
  const offreDemo = /^\/offres\/([^/]+)(\/pdf)?$/.exec(chemin_);
  if (offreDemo) {
    const offre = offresDemo.find((o) => o.id === offreDemo[1]);
    if (!offre) return undefined;
    return offreDemo[2]
      ? {
          url: DEMO_PDF_DOCUMENT_DATA_URI,
          expireLe: new Date(Date.now() + 300_000).toISOString(),
          expireDansSecondes: 300,
          nomFichier: `offre-${offre.numero}.pdf`,
        }
      : { offre };
  }

  if (chemin_ === '/oeuvres/numero-inventaire') {
    return {
      numeroInventaire: `SAV-${String(new Date().getFullYear())}-${String(DEMO_OEUVRES.length + 1).padStart(3, '0')}`,
    };
  }
  const identifiants = chemin_ === '/oeuvres/identifiants';
  if (chemin_ === '/oeuvres' || identifiants) {
    let oeuvres = DEMO_OEUVRES.filter((oeuvre) => oeuvre.statut !== 'archivee');
    if (params.get('inclureArchivees') === 'true') oeuvres = DEMO_OEUVRES.slice();
    const q = params.get('q');
    if (q) {
      const terme = q.toLowerCase();
      oeuvres = oeuvres.filter(
        (oeuvre) =>
          oeuvre.titre.toLowerCase().includes(terme) ||
          oeuvre.artisteNom.toLowerCase().includes(terme) ||
          oeuvre.numeroInventaire.toLowerCase().includes(terme),
      );
    }
    const statut = params.get('statut');
    if (statut) {
      const statuts = new Set(statut.split(','));
      oeuvres = oeuvres.filter((oeuvre) => statuts.has(oeuvre.statut));
    }
    const artisteId = params.get('artisteId');
    if (artisteId) oeuvres = oeuvres.filter((oeuvre) => oeuvre.artisteId === artisteId);
    const localisationId = params.get('localisationId');
    if (localisationId)
      oeuvres = oeuvres.filter((oeuvre) => oeuvre.localisationId === localisationId);
    if (identifiants) return identifiantsDemo(oeuvres.map((oeuvre) => oeuvre.id));
    return {
      oeuvres,
      pagination: { page: 1, parPage: oeuvres.length || 1, total: oeuvres.length, pages: 1 },
      prixArtisteVisible: true,
      ...(params.get('vignettes') === 'true'
        ? {
            vignettes: {
              expireDansSecondes: 120,
              urls: Object.fromEntries(
                oeuvres.flatMap((o) => {
                  const url = o.mediaPrincipalId ? DEMO_VIGNETTES[o.mediaPrincipalId] : undefined;
                  return url ? [[o.id, url]] : [];
                }),
              ),
            },
          }
        : {}),
    };
  }

  const ficheOeuvre = correspond(chemin_, /^\/oeuvres\/([^/]+)$/);
  if (ficheOeuvre?.[1]) {
    const oeuvre = DEMO_OEUVRES.find((candidate) => candidate.id === ficheOeuvre[1]);
    if (!oeuvre) return undefined;
    const lieu = DEMO_LIEUX.find((candidat) => candidat.id === oeuvre.localisationId) ?? null;
    return {
      oeuvre,
      medias: DEMO_MEDIAS[oeuvre.id] ?? [],
      localisation: lieu ? { id: lieu.id, nom: lieu.nom, type: lieu.type } : null,
      editions: [],
    };
  }

  // ── Fiche PDF et certificats (S4-c, § 19.4, § 19.5) ─────────────────────
  const fichePdf = correspond(chemin_, /^\/oeuvres\/([^/]+)\/fiche\.pdf$/);
  if (fichePdf?.[1]) {
    const oeuvre = DEMO_OEUVRES.find((candidate) => candidate.id === fichePdf[1]);
    if (!oeuvre) return undefined;
    const langue = params.get('langue') ?? 'fr';
    return {
      url: DEMO_PDF_DOCUMENT_DATA_URI,
      expireLe: new Date(Date.now() + 300_000).toISOString(),
      expireDansSecondes: 300,
      nomFichier: `fiche-${oeuvre.slug}-${langue}.pdf`,
    };
  }

  const certificatPdf = /^\/oeuvres\/([^/]+)\/certificats\/([^/]+)\/pdf$/.exec(chemin_);
  if (certificatPdf?.[2]) {
    const certificat = certificatsDemo.find((candidat) => candidat.id === certificatPdf[2]);
    if (!certificat) return undefined;
    return {
      url: DEMO_PDF_DOCUMENT_DATA_URI,
      expireLe: new Date(Date.now() + 300_000).toISOString(),
      expireDansSecondes: 300,
      nomFichier: `certificat-${certificat.numero}.pdf`,
    };
  }

  const certificatsOeuvre = correspond(chemin_, /^\/oeuvres\/([^/]+)\/certificats$/);
  if (certificatsOeuvre?.[1]) {
    const certificats = certificatsDemo
      .filter((certificat) => certificat.oeuvreId === certificatsOeuvre[1])
      .sort((a, b) => b.emisLe.localeCompare(a.emisLe));
    return { certificats, total: certificats.length };
  }

  const historique = correspond(chemin_, /^\/oeuvres\/([^/]+)\/historique$/);
  if (historique?.[1]) {
    return {
      historique: [
        {
          id: 'h-1',
          action: 'creation',
          resume: 'Œuvre créée sous ce numéro d’inventaire.',
          acteurEmail: 'direction@sainte-avoye.example',
          date: '2024-05-01T09:00:00.000Z',
        },
      ],
      pagination: { page: 1, parPage: 50, total: 1, pages: 1 },
    };
  }

  const editions = correspond(chemin_, /^\/oeuvres\/([^/]+)\/editions$/);
  if (editions?.[1]) {
    const oeuvre = DEMO_OEUVRES.find((candidate) => candidate.id === editions[1]);
    return {
      type: oeuvre?.type ?? 'unique',
      tirage: oeuvre?.tirage ?? null,
      compteur: oeuvre?.tirage
        ? {
            total:
              oeuvre.tirage.taille + oeuvre.tirage.epreuvesArtiste + oeuvre.tirage.epreuvesEssai,
            vendus: oeuvre.tirage.vendus,
            disponibles: Math.max(
              0,
              oeuvre.tirage.taille +
                oeuvre.tirage.epreuvesArtiste +
                oeuvre.tirage.epreuvesEssai -
                oeuvre.tirage.vendus,
            ),
          }
        : { total: 0, vendus: 0, disponibles: 0 },
      editions: [],
    };
  }

  if (chemin_ === '/artistes') {
    let artistes = DEMO_ARTISTES.filter((artiste) => !artiste.archive);
    const q = params.get('q');
    if (q) {
      const terme = q.toLowerCase();
      artistes = artistes.filter((artiste) => artiste.nom.toLowerCase().includes(terme));
    }
    return {
      artistes: artistes.map(avecNombreOeuvres),
      pagination: { page: 1, parPage: 100, total: artistes.length, pages: 1 },
      ...(params.get('portraits') === 'true'
        ? { portraits: { expireDansSecondes: 120, urls: {} } }
        : {}),
    };
  }
  const ficheArtiste = correspond(chemin_, /^\/artistes\/([^/]+)$/);
  if (ficheArtiste?.[1]) {
    const artiste = DEMO_ARTISTES.find((candidat) => candidat.id === ficheArtiste[1]);
    return artiste ? { artiste: avecNombreOeuvres(artiste) } : undefined;
  }

  if (chemin_ === '/contacts') {
    let contacts = DEMO_CONTACTS.filter((contact) => !contact.archive && !contact.anonymise);
    if (params.get('inclureArchives') === 'true') contacts = DEMO_CONTACTS.slice();
    const q = params.get('q');
    if (q) {
      const terme = q.toLowerCase();
      contacts = contacts.filter(
        (contact) =>
          contact.nomAffiche.toLowerCase().includes(terme) ||
          contact.emails.some((email) => email.toLowerCase().includes(terme)),
      );
    }
    const type = params.get('type');
    if (type) {
      const types = new Set(type.split(','));
      contacts = contacts.filter((contact) => types.has(contact.type));
    }
    const etiquette = params.get('etiquette');
    if (etiquette) contacts = contacts.filter((contact) => contact.etiquettes.includes(etiquette));
    const origine = params.get('origine');
    if (origine) contacts = contacts.filter((contact) => contact.origine === origine);
    const consentement = params.get('consentement');
    if (consentement) {
      const accorde = consentement === 'true';
      contacts = contacts.filter((contact) => contact.consentementNewsletter.accorde === accorde);
    }
    return {
      contacts,
      pagination: { page: 1, parPage: contacts.length || 1, total: contacts.length, pages: 1 },
    };
  }
  // ── Dossier de portabilité en PDF (S4-c, § 19.7) ────────────────────────
  const exportContactPdf = correspond(chemin_, /^\/contacts\/([^/]+)\/export\.pdf$/);
  if (exportContactPdf?.[1]) {
    const contact = DEMO_CONTACTS.find((candidat) => candidat.id === exportContactPdf[1]);
    if (!contact) return undefined;
    const langue = params.get('langue') ?? 'fr';
    return {
      url: DEMO_PDF_DOCUMENT_DATA_URI,
      expireLe: new Date(Date.now() + 300_000).toISOString(),
      expireDansSecondes: 300,
      nomFichier: `contact-${contact.id}-${langue}.pdf`,
    };
  }

  const ficheContact = correspond(chemin_, /^\/contacts\/([^/]+)$/);
  if (ficheContact?.[1]) {
    const contact = DEMO_CONTACTS.find((candidat) => candidat.id === ficheContact[1]);
    if (!contact) return undefined;
    const demandes = DEMO_DEMANDES.filter((demande) => demande.contactId === contact.id);
    const aTraiter = demandes.filter((demande) => demande.etat === 'a_traiter').length;
    return {
      contact,
      agregats: {
        demandes: { total: demandes.length, aTraiter },
        reservations: { total: 0, actives: 0 },
        oeuvresDInteret: demandes
          .filter((demande) => demande.oeuvreId !== null)
          .map((demande) => {
            const oeuvre = DEMO_OEUVRES.find((candidat) => candidat.id === demande.oeuvreId);
            return {
              id: demande.oeuvreId as string,
              titre: oeuvre?.titre ?? '',
              statut: oeuvre?.statut ?? 'disponible',
              origine: 'demande',
            };
          }),
      },
      demandes: demandes.map((demande) => ({
        id: demande.id,
        sujet: demande.sujet,
        etat: demande.etat,
        source: demande.source,
        oeuvreId: demande.oeuvreId,
        recueLe: demande.recueLe,
      })),
      reservations: [],
    };
  }

  if (chemin_ === '/demandes') {
    let demandes = DEMO_DEMANDES.slice();
    const q = params.get('q');
    if (q) {
      const terme = q.toLowerCase();
      demandes = demandes.filter(
        (demande) =>
          demande.nom.toLowerCase().includes(terme) ||
          demande.email.toLowerCase().includes(terme) ||
          demande.sujet.toLowerCase().includes(terme),
      );
    }
    const etat = params.get('etat');
    if (etat) {
      const etats = new Set(etat.split(','));
      demandes = demandes.filter((demande) => etats.has(demande.etat));
    }
    const assigneeA = params.get('assigneeA');
    if (assigneeA) demandes = demandes.filter((demande) => demande.assigneeA === assigneeA);
    const assignation = params.get('assignation');
    if (assignation === 'aucune')
      demandes = demandes.filter((demande) => demande.assigneeA === null);
    if (assignation === 'toute')
      demandes = demandes.filter((demande) => demande.assigneeA !== null);
    const contactId = params.get('contactId');
    if (contactId) demandes = demandes.filter((demande) => demande.contactId === contactId);
    return {
      demandes,
      aTraiter: DEMO_DEMANDES.filter((demande) => demande.etat === 'a_traiter').length,
      pagination: { page: 1, parPage: demandes.length || 1, total: demandes.length, pages: 1 },
    };
  }

  if (chemin_ === '/types-contact') return { fixes: [], personnalises: [] };
  if (chemin_ === '/lieux') {
    return {
      lieux: DEMO_LIEUX,
      pagination: { page: 1, parPage: 200, total: DEMO_LIEUX.length, pages: 1 },
    };
  }
  const ficheLieu = correspond(chemin_, /^\/lieux\/([^/]+)$/);
  if (ficheLieu?.[1]) {
    const lieu = DEMO_LIEUX.find((candidat) => candidat.id === ficheLieu[1]);
    return lieu ? { lieu } : undefined;
  }

  if (chemin_ === '/expositions') {
    let expositions = DEMO_EXPOSITIONS.filter((exposition) => !exposition.archive);
    if (params.get('inclureArchivees') === 'true') expositions = DEMO_EXPOSITIONS.slice();
    const type = params.get('type');
    if (type) expositions = expositions.filter((exposition) => exposition.type === type);
    const etat = params.get('etat');
    if (etat) {
      const etats = new Set(etat.split(','));
      expositions = expositions.filter((exposition) => etats.has(exposition.etat));
    }
    const q = params.get('q');
    if (q) {
      const terme = q.toLowerCase();
      expositions = expositions.filter((exposition) =>
        exposition.titre.fr.toLowerCase().includes(terme),
      );
    }
    return {
      expositions,
      pagination: {
        page: 1,
        parPage: expositions.length || 1,
        total: expositions.length,
        pages: 1,
      },
    };
  }

  // ── Liste de prix en PDF (S4-c, § 19.6) ─────────────────────────────────
  const listeDePrixExpositionPdf = correspond(
    chemin_,
    /^\/expositions\/([^/]+)\/liste-de-prix\.pdf$/,
  );
  if (listeDePrixExpositionPdf?.[1]) {
    const exposition = DEMO_EXPOSITIONS.find(
      (candidate) => candidate.id === listeDePrixExpositionPdf[1],
    );
    if (!exposition) return undefined;
    const langue = params.get('langue') ?? 'fr';
    return {
      url: DEMO_PDF_DOCUMENT_DATA_URI,
      expireLe: new Date(Date.now() + 300_000).toISOString(),
      expireDansSecondes: 300,
      nomFichier: `liste-de-prix-${exposition.slug}-${langue}.pdf`,
    };
  }

  if (chemin_ === '/ventes/liste-de-prix.pdf') {
    const oeuvres = (params.get('oeuvres') ?? '')
      .split(',')
      .map((valeur) => valeur.trim())
      .filter((valeur) => valeur.length > 0);
    if (oeuvres.length === 0) return undefined;
    const langue = params.get('langue') ?? 'fr';
    return {
      url: DEMO_PDF_DOCUMENT_DATA_URI,
      expireLe: new Date(Date.now() + 300_000).toISOString(),
      expireDansSecondes: 300,
      nomFichier: `liste-de-prix-selection-${langue}.pdf`,
    };
  }

  const ficheExposition = correspond(chemin_, /^\/expositions\/([^/]+)$/);
  if (ficheExposition?.[1]) {
    const exposition = DEMO_EXPOSITIONS.find((candidate) => candidate.id === ficheExposition[1]);
    if (!exposition) return undefined;
    const lieu = DEMO_LIEUX.find((candidat) => candidat.id === exposition.lieuId) ?? null;
    const artistes = DEMO_ARTISTES.filter((artiste) => exposition.artistes.includes(artiste.id));
    const oeuvres = DEMO_OEUVRES.filter((oeuvre) => exposition.oeuvres.includes(oeuvre.id));
    return {
      exposition,
      lieu: lieu ? { id: lieu.id, nom: lieu.nom, type: lieu.type } : null,
      artistes: exposition.artistes
        .map((id) => artistes.find((artiste) => artiste.id === id))
        .filter((artiste) => artiste !== undefined)
        .map((artiste) => ({ id: artiste.id, nom: artiste.nom, slug: artiste.slug })),
      oeuvres: exposition.oeuvres
        .map((id) => oeuvres.find((oeuvre) => oeuvre.id === id))
        .filter((oeuvre) => oeuvre !== undefined)
        .map((oeuvre) => ({
          id: oeuvre.id,
          titre: oeuvre.titre,
          artisteNom: oeuvre.artisteNom,
          numeroInventaire: oeuvre.numeroInventaire,
          statut: oeuvre.statut,
          mediaPrincipalId: oeuvre.mediaPrincipalId,
        })),
      vuesAccrochage: [],
    };
  }

  if (chemin_ === '/reservations') {
    let reservations = [...DEMO_RESERVATIONS, ...reservationsOffresDemo];
    const etat = params.get('etat');
    if (etat) {
      const etats = new Set(etat.split(','));
      reservations = reservations.filter((reservation) => etats.has(reservation.etat));
    }
    const oeuvreId = params.get('oeuvreId');
    if (oeuvreId) reservations = reservations.filter((r) => r.oeuvreId === oeuvreId);
    const contactId = params.get('contactId');
    if (contactId) reservations = reservations.filter((r) => r.contactId === contactId);
    if (params.get('echues') === 'true') {
      const maintenant = Date.now();
      reservations = reservations.filter(
        (r) => r.etat === 'active' && new Date(r.jusquau).getTime() <= maintenant,
      );
    }
    reservations = [...reservations].sort((a, b) =>
      params.get('tri') === 'recent'
        ? b.creeeLe.localeCompare(a.creeeLe)
        : a.jusquau.localeCompare(b.jusquau),
    );
    return {
      reservations,
      actives: DEMO_RESERVATIONS.filter((r) => r.etat === 'active').length,
      pagination: {
        page: 1,
        parPage: reservations.length || 1,
        total: reservations.length,
        pages: 1,
      },
    };
  }

  if (chemin_ === '/actualites') {
    const expositionId = params.get('expositionId');
    const actualites = expositionId
      ? DEMO_ACTUALITES.filter((actualite) => actualite.expositions.includes(expositionId))
      : DEMO_ACTUALITES.slice();
    return {
      actualites,
      pagination: { page: 1, parPage: actualites.length || 1, total: actualites.length, pages: 1 },
    };
  }

  const media = correspond(chemin_, /^\/medias\/([^/]+)$/);
  if (media?.[1]) {
    const url = DEMO_VIGNETTES[media[1]];
    if (!url) return undefined;
    const oeuvre = DEMO_OEUVRES.find((candidat) => candidat.mediaPrincipalId === media[1]);
    return {
      media: {
        id: media[1],
        cleOriginale: `demo/${media[1]}.jpg`,
        contentType: 'image/svg+xml',
        nomFichier: `${oeuvre?.slug ?? media[1]}.svg`,
        octets: 0,
        largeur: 640,
        hauteur: 800,
        variantes: [],
        type: 'vue',
        rattachement: { type: 'artwork', id: oeuvre?.id ?? '' },
        credit: null,
        legende: oeuvre ? { fr: oeuvre.titre } : null,
        ordre: 0,
        supprime: false,
        creeLe: '2024-01-01T09:00:00.000Z',
        modifieLe: '2024-01-01T09:00:00.000Z',
      },
      apercu: { url, expireDansSecondes: 120 },
    };
  }

  if (chemin_ === '/ventes/profil-fiscal') {
    return { profilFiscal: DEMO_PROFIL_FISCAL };
  }

  // Archive des factures (D21) : les seules pièces ÉMISES, en lecture seule,
  // tirées des mêmes factures fictives que Ventes.
  if (chemin_ === '/archive-factures') {
    const factures = DEMO_FACTURES.filter((facture) => facture.etat !== 'brouillon').sort((a, b) =>
      (b.emiseLe ?? b.creeLe).localeCompare(a.emiseLe ?? a.creeLe),
    );
    return {
      factures,
      pagination: { page: 1, parPage: 50, total: factures.length, pages: 1 },
    };
  }
  const ficheArchivee = correspond(chemin_, /^\/archive-factures\/([^/]+)$/);
  if (ficheArchivee?.[1]) {
    const facture = DEMO_FACTURES.find(
      (candidate) => candidate.id === ficheArchivee[1] && candidate.etat !== 'brouillon',
    );
    if (!facture) return undefined;
    return {
      facture,
      reglements: DEMO_REGLEMENTS.filter((reglement) => reglement.factureId === facture.id),
      avoirs: DEMO_FACTURES.filter((candidate) => candidate.factureOrigineId === facture.id).map(
        (avoir) => ({
          id: avoir.id,
          numero: avoir.numero,
          emiseLe: avoir.emiseLe,
          totalTtcCentimes: avoir.totaux.totalTtcCentimes,
        }),
      ),
    };
  }
  const pdfArchive = correspond(chemin_, /^\/archive-factures\/([^/]+)\/pdf$/);
  if (pdfArchive?.[1]) {
    const facture = DEMO_FACTURES.find((candidate) => candidate.id === pdfArchive[1]);
    if (!facture || !facture.pdfDisponible) return undefined;
    return {
      numero: facture.numero ?? facture.id,
      url: DEMO_PDF_FACTURE_DATA_URI,
      expireLe: new Date(Date.now() + 300_000).toISOString(),
      expireDansSecondes: 300,
    };
  }

  if (chemin_ === '/ventes/factures') {
    let factures = [...DEMO_FACTURES, ...facturesOffresDemo];
    const type = params.get('type');
    if (type) factures = factures.filter((facture) => facture.type === type);
    const etat = params.get('etat');
    if (etat) {
      const etats = new Set(etat.split(','));
      factures = factures.filter((facture) => etats.has(facture.etat));
    }
    const contactId = params.get('contactId');
    if (contactId) factures = factures.filter((facture) => facture.contactId === contactId);
    const annee = params.get('annee');
    if (annee) {
      factures = factures.filter(
        (facture) => facture.emiseLe !== null && facture.emiseLe.startsWith(annee),
      );
    }
    const q = params.get('q');
    if (q) {
      const terme = q.toLowerCase();
      factures = factures.filter((facture) => (facture.numero ?? '').toLowerCase().includes(terme));
    }
    if (params.get('impayees') === 'true') {
      factures = factures.filter(
        (facture) => facture.type === 'facture' && facture.etat === 'emise',
      );
    }
    const tri = params.get('tri') ?? 'recent';
    factures = [...factures].sort((a, b) => {
      if (tri === 'numero') return (a.numero ?? '').localeCompare(b.numero ?? '');
      if (tri === 'montant') return b.totaux.totalTtcCentimes - a.totaux.totalTtcCentimes;
      const dateA = a.emiseLe ?? a.creeLe;
      const dateB = b.emiseLe ?? b.creeLe;
      return tri === 'ancien' ? dateA.localeCompare(dateB) : dateB.localeCompare(dateA);
    });
    return {
      factures,
      pagination: { page: 1, parPage: factures.length || 1, total: factures.length, pages: 1 },
      oeuvresVisibles: true,
    };
  }

  const ficheFacture = correspond(chemin_, /^\/ventes\/factures\/([^/]+)$/);
  if (ficheFacture?.[1]) {
    const facture = [...DEMO_FACTURES, ...facturesOffresDemo].find(
      (candidate) => candidate.id === ficheFacture[1],
    );
    if (!facture) return undefined;
    return {
      facture,
      reglements: DEMO_REGLEMENTS.filter((reglement) => reglement.factureId === facture.id),
      avoirs: DEMO_FACTURES.filter((candidate) => candidate.factureOrigineId === facture.id).map(
        (avoir) => ({
          id: avoir.id,
          numero: avoir.numero,
          emiseLe: avoir.emiseLe,
          totalTtcCentimes: avoir.totaux.totalTtcCentimes,
        }),
      ),
      oeuvresVisibles: true,
    };
  }

  const pdfFacture = correspond(chemin_, /^\/ventes\/factures\/([^/]+)\/pdf$/);
  if (pdfFacture?.[1]) {
    const facture = DEMO_FACTURES.find((candidate) => candidate.id === pdfFacture[1]);
    if (!facture || !facture.pdfDisponible) return undefined;
    return {
      numero: facture.numero ?? facture.id,
      url: DEMO_PDF_FACTURE_DATA_URI,
      expireLe: new Date(Date.now() + 300_000).toISOString(),
      expireDansSecondes: 300,
    };
  }

  if (chemin_ === '/ventes/reglements') {
    const factureId = params.get('factureId');
    const reglements = factureId
      ? DEMO_REGLEMENTS.filter((reglement) => reglement.factureId === factureId)
      : DEMO_REGLEMENTS;
    return {
      reglements,
      totalCentimes: reglements.reduce((somme, reglement) => somme + reglement.montantCentimes, 0),
      pagination: { page: 1, parPage: reglements.length || 1, total: reglements.length, pages: 1 },
    };
  }

  // ── Coffre (§ 18, S5) ────────────────────────────────────────────────────
  // Déclarées comme côté serveur : `/echeances` et `/journal/export` avant
  // `/:id`, sans quoi la première capturerait la seconde (§ 18.1).

  if (chemin_ === '/documents/echeances') {
    const horizonJours = Number(params.get('horizonJours') ?? '90');
    const horizon = new Date(Date.now() + horizonJours * 24 * 60 * 60 * 1000);
    const echeances = DEMO_DOCUMENTS.filter(
      (document) => !document.archive && document.echeanceLe !== null,
    )
      .filter((document) => new Date(document.echeanceLe as string) <= horizon)
      .map((document) => ({
        id: document.id,
        titre: document.titre,
        type: document.type,
        confidentialite: document.confidentialite,
        echeanceLe: document.echeanceLe as string,
        joursRestants: joursCivilsRestants(new Date(document.echeanceLe as string)),
      }))
      .sort((a, b) => a.joursRestants - b.joursRestants);
    return {
      echeances: {
        aVenir: echeances.filter((echeance) => echeance.joursRestants >= 0),
        enRetard: echeances.filter((echeance) => echeance.joursRestants < 0),
      },
      horizonJours,
    };
  }

  // `journal/export` (§ 18.6) est un `requeteTelechargement` : il ne passe
  // jamais par ce dispatcheur (comme l'export comptable de 17.10), rien à y
  // simuler.

  const journalDocument = correspond(chemin_, /^\/documents\/([^/]+)\/journal$/);
  if (journalDocument?.[1]) {
    const document = DEMO_DOCUMENTS.find((candidat) => candidat.id === journalDocument[1]);
    if (!document) return undefined;
    const journal = DEMO_DOCUMENT_ACCESS.filter((entree) => entree.documentId === document.id);
    return {
      document: { id: document.id, titre: document.titre },
      journal,
      retentionJours: 365,
      pagination: { page: 1, parPage: journal.length || 1, total: journal.length, pages: 1 },
    };
  }

  const telechargementDocument = correspond(chemin_, /^\/documents\/([^/]+)\/telechargement$/);
  if (telechargementDocument?.[1]) {
    const document = DEMO_DOCUMENTS.find((candidat) => candidat.id === telechargementDocument[1]);
    if (!document) return undefined;
    return {
      telechargement: {
        url: 'data:application/pdf;base64,JVBERi0xLjQKJeLjz9MK',
        nom: document.fichier.nom,
        typeMime: document.fichier.typeMime,
        octets: document.fichier.octets,
        expireLe: new Date(Date.now() + 120_000).toISOString(),
        expireDansSecondes: 120,
      },
    };
  }

  if (methode === 'GET') {
    if (chemin_ === '/locations')
      return { contrats: [], pagination: { page: 1, parPage: 50, total: 0, pages: 0 } };
    if (chemin_ === '/locations/vue-ensemble')
      return {
        mrrHtCentimes: 0,
        serieMrr: [],
        oeuvresLouees: [],
        echeancesAVenir: [],
        optionsOuvertes: [],
      };
    if (chemin_ === '/locations/echeances')
      return { a_facturer: [], emises: [], impayees: [], a_rattraper: [] };
    if (chemin_ === '/locations/reglages') return { reglages: { emissionAutomatique: false } };
    if (/^\/oeuvres\/[^/]+\/locations$/.test(chemin_)) return { contrats: [] };
  }
  if (chemin_ === '/mouvements' && methode === 'GET') {
    let mouvements = DEMO_MOUVEMENTS.slice();
    const type = params.get('type');
    if (type) mouvements = mouvements.filter((m) => m.type === type);
    const etat = params.get('etat');
    if (etat) {
      const etats = new Set(etat.split(','));
      mouvements = mouvements.filter((m) => etats.has(m.etat));
    }
    if (params.get('enRetard') === 'true') mouvements = mouvements.filter((m) => m.enRetard);
    const oeuvreId = params.get('oeuvreId');
    if (oeuvreId)
      mouvements = mouvements.filter((m) => m.lignes.some((l) => l.oeuvreId === oeuvreId));
    return {
      mouvements,
      pagination: { page: 1, parPage: mouvements.length || 1, total: mouvements.length, pages: 1 },
    };
  }
  const ficheMouvement = correspond(chemin_, /^\/mouvements\/([^/]+)$/);
  if (ficheMouvement?.[1] && methode === 'GET') {
    const mouvement = DEMO_MOUVEMENTS.find((candidat) => candidat.id === ficheMouvement[1]);
    return mouvement ? { mouvement } : undefined;
  }
  if (correspond(chemin_, /^\/mouvements\/[^/]+\/(bon\.pdf|constats\/[^/]+\/pdf)$/)) {
    return {
      url: DEMO_PDF_DOCUMENT_DATA_URI,
      expireLe: new Date(Date.now() + 300_000).toISOString(),
      expireDansSecondes: 300,
      nomFichier: 'bon-demo.pdf',
    };
  }

  if (chemin_ === '/documents') {
    let documents = DEMO_DOCUMENTS.slice();
    if (params.get('inclureArchives') !== 'true') {
      documents = documents.filter((document) => !document.archive);
    }
    const type = params.get('type');
    if (type) {
      const types = new Set(type.split(','));
      documents = documents.filter((document) => types.has(document.type));
    }
    const rattache = params.get('rattache');
    if (rattache) {
      const [cibleBrute, id] = rattache.split(':');
      const cible = ALIAS_RATTACHEMENT_DEMO[cibleBrute ?? ''] ?? cibleBrute;
      documents = documents.filter((document) =>
        document.rattachements.some((r) => r.type === cible && r.id === id),
      );
    }
    const confidentialite = params.get('confidentialite');
    if (confidentialite) {
      documents = documents.filter((document) => document.confidentialite === confidentialite);
    }
    const etiquette = params.get('etiquette');
    if (etiquette) {
      documents = documents.filter((document) => document.etiquettes.includes(etiquette));
    }
    const q = params.get('q');
    if (q) {
      const terme = q.toLowerCase();
      documents = documents.filter((document) => document.titre.toLowerCase().includes(terme));
    }
    const tri = params.get('tri') ?? 'recent';
    documents = [...documents].sort((a, b) => {
      if (tri === 'titre') return a.titre.localeCompare(b.titre);
      if (tri === 'echeance') {
        if (a.echeanceLe === null) return 1;
        if (b.echeanceLe === null) return -1;
        return a.echeanceLe.localeCompare(b.echeanceLe);
      }
      return tri === 'ancien' ? a.creeLe.localeCompare(b.creeLe) : b.creeLe.localeCompare(a.creeLe);
    });
    return {
      documents,
      // La session de démonstration est « direction » (droit fin complet) :
      // rien n'est jamais masqué (§ 18.2).
      confidentielsMasques: 0,
      pagination: { page: 1, parPage: documents.length || 1, total: documents.length, pages: 1 },
    };
  }

  const ficheDocument = correspond(chemin_, /^\/documents\/([^/]+)$/);
  if (ficheDocument?.[1]) {
    const document = DEMO_DOCUMENTS.find((candidat) => candidat.id === ficheDocument[1]);
    if (!document) return undefined;
    return { document };
  }

  if (chemin_ === '/equipe' && methode === 'GET') {
    return {
      galerie: DEMO_GALERIE,
      membres: equipeDemo.membres,
      revision: equipeDemo.revision,
      droitsAppliques: true,
    };
  }

  if (chemin_ === '/sso/identites' && methode === 'GET') return { identites: identitesSsoDemo };
  if (chemin_ === '/connecteurs' && methode === 'GET') {
    const avecSso = modulesDeDemonstration().includes('sso');
    return {
      ...DEMO_CONNECTEURS,
      champs: avecSso ? DEMO_CONNECTEURS.champs : {},
      connecteurs: [
        ...(avecSso ? DEMO_CONNECTEURS.connecteurs : []),
        ...(modulesDeDemonstration().includes('artsy-artnet')
          ? [
              {
                id: 'artsy',
                module: 'artsy-artnet',
                mode: 'simule',
                configure: true,
                finSecret: null,
                parametres: { depot: 'url' },
                verifieLe: null,
                erreur: null,
                revision: 1,
              },
            ]
          : []),
      ],
    };
  }

  return undefined;
}

/**
 * Jours civils restants avant une échéance, comptés depuis aujourd'hui (même
 * règle que `apps/api/src/coffre/echeances.ts`, § 18.7) : un 1er mars à 9 h vu
 * le 28 février à 23 h est à UN jour, pas à zéro.
 */
function joursCivilsRestants(echeance: Date): number {
  const maintenant = new Date();
  const jourEcheance = Date.UTC(echeance.getFullYear(), echeance.getMonth(), echeance.getDate());
  const jourCourant = Date.UTC(
    maintenant.getFullYear(),
    maintenant.getMonth(),
    maintenant.getDate(),
  );
  return Math.round((jourEcheance - jourCourant) / (24 * 60 * 60 * 1000));
}
